import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { Button, Spinner } from 'react-bootstrap';
import { BaseMap } from './BaseMap';
import { FeatureTooltip } from './FeatureTooltip';
import { CreateFeatureModal, EditFeatureModal } from './FeatureModals';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { SignOutModal } from './SignOutModal';
import { ClubMapBoards } from './ClubMapBoards';
import { ClubLandSetupOverlay } from './ClubLandSetupOverlay';
import { ClubConfirmViewBar } from './ClubConfirmViewBar';
import { MyGroundBoard } from './MyGroundBoard';
import './StandBoardList.css';
import { WeatherWidget } from './WeatherWidget';
import { ClubMapLegend } from './ClubMapLegend';
import { MarkerModal } from './MarkerModal';
import { fetchEntitlementsFromProfile } from '../../pro/ProGate';
import { hasEntitlement } from '../../pro/entitlements';
import { ProPanel } from '../../pro/ProPanel';
import { weatherService } from '../services/weatherService';
import { useMapFeatures } from '../hooks/useMapFeatures';
import { useClubFeatures } from '../hooks/useClubFeatures';
import { useHuntSignOut } from '../hooks/useHuntSignOut';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import api from '../../../utils/api';
import './UnifiedMapPage.css';
import './mapChromeFieldEdition.css';
import { formatDistance } from '../utils/geometryUtils';
import { createRafCoalescer } from '../utils/rafCoalesce';
import { buildClubHuntLocationPayload, buildPointIdentityPayload, clubDrawnOpensWaypointModal, clubPointUsesWaypointEditor, waypointFormFromFeature, shouldEditAsWaypoint } from '../utils/pointIdentityPayload';
import { clubBoardLocationAsEditFeature, clubHuntLocationNumericId, clubWaypointMovePayload, mapClubFeatureToEndpoint } from '../utils/clubFeatureHelpers';
import { bboxFromAreaGeojson, clubBoardBoundaryAsEditFeature } from '../utils/clubAreaBoard';
import { clubLandDeletePayload, clubLandDeletePlan } from '../utils/clubLandDelete';
import { clubWaypointDeletePlan } from '../utils/clubWaypointDelete';
import { restoreFeatureListIfNeeded } from '../utils/featureListRestore';
import {
  clubLandDrawDefaultName,
  clubTracts,
  huntBoundaryCreatePayload,
  huntBoundaryRoleError,
  huntBoundarySaveError,
  clubTractNamesFromClusters,
  isHuntTract,
  parentHuntLocationToTract,
} from '../utils/clubBoundaryRole';
import { shouldShowClubLandSetup } from '../utils/clubLandSetup';

import { useToast } from '../../../components/ui/ToastProvider';
import { onHuntCompleted, consumePendingHuntCompleted } from '../../../utils/huntEvents';
import { ParcelInfoRequestModal } from './ParcelInfoRequestModal';
import { ParcelIdentifyControl } from './ParcelIdentifyControl';
import { ParcelLotSelectControl } from './ParcelLotSelectControl';
import { ParcelTapDebug, isParcelDebugEnabled } from './ParcelTapDebug';
import { canOfferParcelIdentify, shouldExitParcelIdentify, canOfferLotSelect } from '../utils/parcelIdentifyMode';
import { useHighlightGroup } from '../context/HighlightContext';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { TagOutPicker } from './TagOutPicker';
import { MyGroundMaximizeControl } from './MyGroundMaximizeControl';
import { useIsMobileMd } from '../../../hooks/useMatchMedia';
import { useMobileMapLayout } from '../hooks/useMobileMapLayout';
import { buildSortedClubStands, buildClubStandFeature } from '../utils/clubStandList';
import { partitionClubWaypoints } from '../utils/clubWaypointBoard';
import { clubFillLoggedToast, clubLogFillMemberName } from '../utils/clubLogFill';
import {
  applySelectedFeatureLayers,
  selectedMapFeatureId,
} from '../utils/selectedMapFeature';
import feedingService from '../services/feedingService';
import {
  buildOccupancyFromStandRow,
  buildOccupancyFromSignOut,
  resolveHuntLocationNumericId,
} from '../utils/standSheetUtils';
import { DEFAULT_BASE_MAP } from '../config/mapConfig';
import { toLocalYMD } from '../../../utils/dateUtils';
import { buildPickerHighlightEaseOptions, clearMapPadding } from '../utils/mapPickerCamera';
import {
  resolveClubBoundaryBbox,
  clubBoundaryCenterFromBbox,
  clubBoundaryFitBoundsOptions,
  resolveClubMapState,
  hasCanonicalClubViewport,
  clubMapConfigFromCamera,
  buildClubJumpToOptions,
  writeClubCameraCache,
} from '../utils/clubMapCamera';
import {
  applyClubViewExtent,
  clearClubViewExtent,
  clubBrowseExtentActive,
  clubCameraStateFromMap,
  hasSavedClubView,
  shouldEnterConfirmClubView,
} from '../utils/clubConfirmView';
import { notifyInTheStandChanged } from '../../../hooks/useInTheStandCount';
import { areaDraftType, geometriesForUnion } from '../utils/parcelSelection';
import { ensureMapMaximized } from '../utils/ensureMapMaximized';

export function UnifiedMapPage({
  showFeatureList,
  onCloseFeatureList,
  onShowFeatureList,
  mapMaximized = false,
  onToggleMapMaximize = null,
  /** Club detail Map tab: framed map + STAND BOARD list below */
  embedStandBoard = false,
}) {
  const { clubId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mapInstanceRef = useRef(null);
  const drawInstanceRef = useRef(null);
  const featureListOpenBeforeEditorRef = useRef(false);
  const { currentUser, getDisplayName } = useCurrentUser();
  const { show } = useToast();
  const { highlightGroup, clearHighlightGroup } = useHighlightGroup();
  const isMobileMd = useIsMobileMd();
  const [canEnableFeed, setCanEnableFeed] = useState(true);
  const [canEnableParcels, setCanEnableParcels] = useState(false);
  const [showParcelsPro, setShowParcelsPro] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEntitlementsFromProfile()
      .then((parsed) => {
        if (cancelled) return;
        setCanEnableFeed(hasEntitlement(parsed, 'toolkit'));
        setCanEnableParcels(hasEntitlement(parsed, 'parcels'));
      })
      .catch(() => {
        if (cancelled) return;
        setCanEnableFeed(false);
        setCanEnableParcels(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const extendBoundsWithGeometry = useCallback((bounds, geometry) => {
    if (!geometry || !bounds) return;
    const pushCoords = (coords) => {
      if (!Array.isArray(coords)) return;
      coords.forEach((coord) => {
        if (Array.isArray(coord) && typeof coord[0] === 'number' && typeof coord[1] === 'number') {
          bounds.extend(coord);
        } else if (Array.isArray(coord)) {
          pushCoords(coord);
        }
      });
    };
    try {
      switch (geometry.type) {
        case 'Point':
          bounds.extend(geometry.coordinates);
          break;
        case 'LineString':
        case 'MultiLineString':
        case 'Polygon':
        case 'MultiPolygon':
          pushCoords(geometry.coordinates);
          break;
        case 'GeometryCollection':
          if (Array.isArray(geometry.geometries)) {
            geometry.geometries.forEach((g) => extendBoundsWithGeometry(bounds, g));
          }
          break;
        default:
          break;
      }
    } catch (_) {}
  }, []);
  
  // Determine map type
  const mapType = clubId ? 'club' : 'personal';
  
  // Club data state
  const [clubData, setClubData] = useState(null);
  
  // Add weather widget state
  const [mapCenter, setMapCenter] = useState({ lat: 32.8067, lng: -86.7911 }); // Default to Alabama center
  const [currentWindDeg, setCurrentWindDeg] = useState(null);
  
  // Personal features hook
  const {
    features: personalFeatures,
    showCreateModal,
    showEditModal,
    showDeleteModal,
    draftFeature,
    editingFeature,
    featureToDelete,
    draftName,
    setDraftName,
    draftNotes,
    setDraftNotes,
    draftIcon,
    setDraftIcon,
    draftRadius,
    setDraftRadius,
    draftOptimalWinds,
    setDraftOptimalWinds,
    draftIsStand,
    setDraftIsStand,
    draftLineColor,
    setDraftLineColor,
    draftFillColor,
    setDraftFillColor,
    draftFillOpacity,
    setDraftFillOpacity,
    editName,
    setEditName,
    editNotes,
    setEditNotes,
    editIcon,
    setEditIcon,
    editRadius,
    setEditRadius,
    editOptimalWind,
    setEditOptimalWind,
    editOptimalWinds,
    setEditOptimalWinds,
    editIsStand,
    setEditIsStand,
    editLineColor,
    setEditLineColor,
    editFillColor,
    setEditFillColor,
    editFillOpacity,
    setEditFillOpacity,
    editFeedingMode,
    setEditFeedingMode,
    editFeedCapacityLbs,
    setEditFeedCapacityLbs,
    editConsumptionRateCode,
    setEditConsumptionRateCode,
    editRefillThresholdPct,
    setEditRefillThresholdPct,
    listFeatures,
    handleFeatureCreate,
    handleCancelCreate,
    handleSaveCreate,
    handleEditFeature,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete
  } = useMapFeatures();
  // Track which feature is being geometry-edited (personal context)
  const [geometryEditingFeature, setGeometryEditingFeature] = useState(null);

  // Add club feature creation state
  const [clubDraftFeature, setClubDraftFeature] = useState(null);
  const [clubDraftName, setClubDraftName] = useState('');
  const [clubDraftNotes, setClubDraftNotes] = useState('');
  const [clubDraftIcon, setClubDraftIcon] = useState('default');
  const [clubDraftRadius, setClubDraftRadius] = useState(0);
  const [clubDraftLineColor, setClubDraftLineColor] = useState('');
  const [clubDraftFillColor, setClubDraftFillColor] = useState('');
  const [clubDraftFillOpacity, setClubDraftFillOpacity] = useState(0);
  const [showClubCreateModal, setShowClubCreateModal] = useState(false);
  // Add missing club-specific state variables
  const [clubDraftFeatureType, setClubDraftFeatureType] = useState('stand');
  const [clubDraftOptimalWinds, setClubDraftOptimalWinds] = useState([]);
  const [clubDraftIsBoundary, setClubDraftIsBoundary] = useState(true);
  const [clubLandPath, setClubLandPath] = useState(null);
  const [clubConfirmingView, setClubConfirmingView] = useState(false);
  const [clubViewSaving, setClubViewSaving] = useState(false);
  const [clubParcelClusters, setClubParcelClusters] = useState([]);
  const [clubLandDeleteTarget, setClubLandDeleteTarget] = useState(null);
  const [clubLandDeleteBusy, setClubLandDeleteBusy] = useState(false);
  const [clubWaypointDeleteTarget, setClubWaypointDeleteTarget] = useState(null);
  const [clubWaypointDeleteBusy, setClubWaypointDeleteBusy] = useState(false);

  // Club features hook
  const {
    huntBoundaries,
    huntLocations,
    listClubFeatures,
    getMapFeatures,
    loading: clubFeaturesLoading,
  } = useClubFeatures(clubId);
  const [highlightStandId, setHighlightStandId] = useState(null);
  const [highlightFeatureId, setHighlightFeatureId] = useState(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const highlightStandMarkerRef = useRef(null);
  const isMobileMap = useMobileMapLayout();
  const [showTagOutPicker, setShowTagOutPicker] = useState(false);
  const [pickerFocusedStandId, setPickerFocusedStandId] = useState(null);
  const [pickerSelectedStandId, setPickerSelectedStandId] = useState(null);
  const [pickerSheetHeight, setPickerSheetHeight] = useState(0);

  const clubStands = useMemo(
    () => buildSortedClubStands(huntLocations),
    [huntLocations]
  );
  const clubWaypoints = useMemo(
    () => partitionClubWaypoints(huntLocations),
    [huntLocations]
  );
  const [boardSelectedStandId, setBoardSelectedStandId] = useState(null);
  const [boardSelectedAreaId, setBoardSelectedAreaId] = useState(null);
  const [hoveredWaypointId, setHoveredWaypointId] = useState(null);
  const [clubFeedSeasonLbs, setClubFeedSeasonLbs] = useState(0);
  const activeBaseMapStyle = DEFAULT_BASE_MAP;

  // Hunt sign-out hook
  const {
    loading: signOutLoading,
    error: signOutError,
    updateFeatureStates,
    signOutLocation,
    signInLocation,
    initializeSignOutStates,
    clearError: clearSignOutError
  } = useHuntSignOut();

  // Hunt completion hook removed (unused)

  // State for auto-fit tracking
  const [hasFittedToClubBounds, setHasFittedToClubBounds] = useState(false);
  const [hasFittedToPersonalFeatures, setHasFittedToPersonalFeatures] = useState(false);
  const [clubCameraReady, setClubCameraReady] = useState(() => mapType !== 'club');
  const markClubCameraReady = useCallback(() => {
    setClubCameraReady(true);
  }, []);

  const clubInitialMapConfig = useMemo(() => {
    if (mapType !== 'club') return null;
    const mapInfo = clubData?.map || {};
    const resolved = resolveClubMapState(mapInfo);
    if (hasCanonicalClubViewport(resolved)) {
      return clubMapConfigFromCamera(resolved.state);
    }
    const bbox = resolveClubBoundaryBbox(mapInfo, huntBoundaries);
    const center = clubBoundaryCenterFromBbox(bbox);
    return {
      usePersonalSavedView: false,
      ...(center ? { center, zoom: 12 } : {}),
    };
  }, [mapType, clubData, huntBoundaries]);

  // Basemap remount must re-fit boundary (key includes style); don't leave Alabama default.
  useEffect(() => {
    if (mapType !== 'club') return;
    setHasFittedToClubBounds(false);
    setClubCameraReady(false);
  }, [mapType]);

  // Hunt sign-out / stand-entry log sheet state
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sheetOccupancy, setSheetOccupancy] = useState(null);

  // Drawing mode state
  const [drawingMode] = useState('select');
  const [realTimeDistance, setRealTimeDistance] = useState(null);
  const drawUpdateCoalescerRef = useRef(null);
  if (!drawUpdateCoalescerRef.current) {
    drawUpdateCoalescerRef.current = createRafCoalescer();
  }

  // Creation context and marker modal state
  const [showMarkerModal, setShowMarkerModal] = useState(false);
  const [markerName, setMarkerName] = useState('');
  const [markerNotes, setMarkerNotes] = useState('');
  const [markerIsStand, setMarkerIsStand] = useState(true);
  const [markerFeed, setMarkerFeed] = useState(false);
  const [markerType, setMarkerType] = useState('');
  const [markerWinds, setMarkerWinds] = useState([]);
  const [markerFeedingMode, setMarkerFeedingMode] = useState('');
  const [markerFeedCapacityLbs, setMarkerFeedCapacityLbs] = useState(null);
  const [markerConsumptionRateCode, setMarkerConsumptionRateCode] = useState('');
  const [markerRefillThresholdPct, setMarkerRefillThresholdPct] = useState(20);
  const [markerDraftFeature, setMarkerDraftFeature] = useState(null);
  const [editingMarkerId, setEditingMarkerId] = useState(null);

  // Memoize allFeatures to prevent infinite loops
  const allFeatures = useMemo(() => {
    return mapType === 'club' ? getMapFeatures() : personalFeatures;
  }, [mapType, getMapFeatures, personalFeatures]);
  // Parcel info request modal state
  const [showParcelModal, setShowParcelModal] = useState(false);
  const [pendingParcel, setPendingParcel] = useState(null);
  const handleParcelInfoRequested = useCallback((parcel) => {
    setPendingParcel(parcel);
    setShowParcelModal(true);
  }, []);

  // --- Parcel Identify mode (phone only) ---
  // Parcels on only draws boundaries, so they stay usable for tracing. Identify
  // is armed separately, and only then does a tap resolve to a parcel.
  const [parcelsVisible, setParcelsVisible] = useState(false);
  const [parcelIdentifyOn, setParcelIdentifyOn] = useState(false);
  const toggleParcelIdentify = useCallback(() => setParcelIdentifyOn(prev => !prev), []);

  // --- Parcel selection mode (personal + club) ---
  const [parcelSelectEnabled, setParcelSelectEnabled] = useState(false);
  const [selectedParcels, setSelectedParcels] = useState([]); // array of { parcelId, geometry, properties }
  const [unionPreview, setUnionPreview] = useState(null);
  const [contiguousOk, setContiguousOk] = useState(true);
  const [unionBusy, setUnionBusy] = useState(false);
  const [closeParcelsPopoverSignal, setCloseParcelsPopoverSignal] = useState(0);

  const toggleParcelSelectMode = useCallback((next) => {
    setParcelSelectEnabled(prev => {
      const desired = (typeof next === 'boolean') ? next : !prev;
      if (!desired) {
        setSelectedParcels([]);
        setUnionPreview(null);
        setContiguousOk(true);
      } else if (mapType === 'club' && mapInstanceRef.current) {
        // Saved-view extent would block selecting a second lease.
        clearClubViewExtent(mapInstanceRef.current);
      }
      return desired;
    });
  }, [mapType]);

  // Arming the mode hands the map to the user, so the sheet that armed it gets
  // out of the way: on a phone it covers the lots being picked.
  useEffect(() => {
    if (parcelSelectEnabled) {
      setCloseParcelsPopoverSignal(v => v + 1);
    }
  }, [parcelSelectEnabled]);

  // Shared by the map control and BaseMap, so there is one way out of the mode.
  const exitParcelSelection = useCallback(() => {
    toggleParcelSelectMode(false);
    setSelectedParcels([]);
    setUnionPreview(null);
    setContiguousOk(true);
    if (mapType === 'club' && clubTracts(huntBoundaries).length === 0) {
      setClubLandPath(null);
    }
  }, [toggleParcelSelectMode, mapType, huntBoundaries]);

  const parcelIdentifyConditions = useMemo(() => ({
    isPhone: isMobileMd,
    parcelsVisible,
    lotSelectionOn: parcelSelectEnabled,
    drawingMode
  }), [isMobileMd, parcelsVisible, parcelSelectEnabled, drawingMode]);

  // One owner per tap: arming lot-selection or starting a draw leaves Identify,
  // and turning parcels off removes the mode along with its control.
  useEffect(() => {
    if (shouldExitParcelIdentify({ identifyOn: parcelIdentifyOn, ...parcelIdentifyConditions })) {
      setParcelIdentifyOn(false);
    }
  }, [parcelIdentifyOn, parcelIdentifyConditions]);

  const showParcelIdentifyControl = canOfferParcelIdentify(parcelIdentifyConditions);
  const showLotSelectControl = canOfferLotSelect({
    isPhone: isMobileMd,
    lotSelectionOn: parcelSelectEnabled
  });

  const handleParcelSelectToggle = useCallback((parcel) => {
    if (!parcel || !parcel.parcelId) return;
    setSelectedParcels(prev => {
      const exists = prev.find(p => String(p.parcelId) === String(parcel.parcelId));
      const next = exists
        ? prev.filter(p => String(p.parcelId) !== String(parcel.parcelId))
        : [...prev, parcel];
      // Preview without union: show a collection of selected parcel geometries
      try {
        const features = next.map(p => ({ type: 'Feature', properties: {}, geometry: p.geometry }));
        const fc = { type: 'FeatureCollection', features };
        setUnionPreview(fc);
        setContiguousOk(true); // defer contiguity check to backend on Done
      } catch (_) {
        setUnionPreview(null);
        setContiguousOk(true);
      }
      return next;
    });
  }, []);

  const getClubMapId = useCallback(async (targetClubId) => {
    try {
      const response = await api.get(`/clubs/${targetClubId}/`);
      const clubMapId = response.data.map?.id;
      if (!clubMapId) {
        throw new Error('This club has no map record yet. Refresh and try again.');
      }
      return clubMapId;
    } catch (error) {
      console.error('Failed to get club map ID:', error);
      throw error;
    }
  }, []);

  const handleParcelDone = useCallback(() => {
    (async () => {
      try {
        setUnionBusy(true);
        if (!parcelSelectEnabled || selectedParcels.length === 0) {
          try { console.warn('[parcel] Save clicked but selection is empty or not in selection mode.', { parcelSelectEnabled, count: selectedParcels.length }); } catch(_) {}
          return;
        }
        // Log selection count for debugging
        try { console.log('[parcel] saving boundary from selection count:', selectedParcels.length); } catch (_) {}
        const geometries = geometriesForUnion(selectedParcels, mapInstanceRef.current);
        const requireContiguous = mapType !== 'club';
        const payload = { geometries, require_contiguous: requireContiguous };
        try { console.log('[parcel] POST /union payload', payload); } catch(_) {}
        const res = await api.post('/union/', payload);
        try { console.log('[parcel] union response', res && res.data); } catch(_) {}
        const clusters = Array.isArray(res?.data?.clusters) && res.data.clusters.length > 0
          ? res.data.clusters
          : (res?.data?.geometry ? [res.data.geometry] : []);
        if (clusters.length === 0) throw new Error('No geometry returned');
        if (mapType === 'club') {
          setClubParcelClusters(clusters);
          setClubDraftFeature({
            tempId: `parcel-union-${Date.now()}`,
            geometry: clusters[0],
            type: 'polygon',
          });
          setClubDraftName(clusters.length > 1 ? 'Boundary' : 'Boundary');
          setClubDraftNotes('');
          setClubDraftIsBoundary(true);
          setShowClubCreateModal(true);
        } else {
          const geometry = clusters[0];
          handleFeatureCreate({
            id: `parcel-union-${Date.now()}`,
            geometry,
            type: areaDraftType(geometry),
          });
          try { show({ title: 'Boundary ready', body: 'Review details and save.', variant: 'success' }); } catch(_) {}
        }
        // Exit selection mode
        setParcelSelectEnabled(false);
        setSelectedParcels([]);
        setUnionPreview(null);
        setContiguousOk(true);
        // Close the Parcels popover
        setCloseParcelsPopoverSignal(v => v + 1);
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Failed to create area from parcels';
        try { show({ title: 'Parcels must touch', body: msg, variant: 'warning' }); } catch (_) { alert(msg); }
        setContiguousOk(false);
      } finally {
        setUnionBusy(false);
      }
    })();
  }, [parcelSelectEnabled, selectedParcels, mapType, handleFeatureCreate, show]);

  // Hidden features (session-only toggle)
  const [hiddenFeatureIds, setHiddenFeatureIds] = useState(() => new Set());
  const filteredFeatures = useMemo(() => {
    if (mapType === 'club') return allFeatures;
    if (!allFeatures || hiddenFeatureIds.size === 0) return allFeatures;
    try {
      return allFeatures.filter(f => !hiddenFeatureIds.has(String(f.id)));
    } catch (_) {
      return allFeatures;
    }
  }, [allFeatures, hiddenFeatureIds, mapType]);

  // Initialize sign-out states when features change
  useEffect(() => {
    if (mapType === 'club' && allFeatures.length > 0) {
      initializeSignOutStates(allFeatures);
    }
  }, [mapType, allFeatures, initializeSignOutStates]);

  // Maximize: Mapbox must resize after chrome header hides/shows.
  // Club map sits several flex wrappers down — wait two frames so the
  // frame has its stretched height before canvas size is locked.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || typeof map.resize !== 'function') return undefined;
    const run = () => {
      try {
        map.resize();
      } catch (_) {}
    };
    let innerId = 0;
    const id = window.requestAnimationFrame(() => {
      run();
      innerId = window.requestAnimationFrame(run);
    });
    const timeoutId = window.setTimeout(run, 50);
    return () => {
      window.cancelAnimationFrame(id);
      window.cancelAnimationFrame(innerId);
      window.clearTimeout(timeoutId);
    };
  }, [mapMaximized, mapType]);

  // Escape exits maximize
  useEffect(() => {
    if (!mapMaximized || typeof onToggleMapMaximize !== 'function') {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onToggleMapMaximize();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mapMaximized, mapType, onToggleMapMaximize]);

  // Subscribe to hunt completed to refresh club features (so markers turn blue)
  useEffect(() => {
    if (mapType !== 'club') return undefined;
    const unsubscribe = onHuntCompleted(async () => {
      try {
        await listClubFeatures();
      } catch (_) {}
    });
    return () => { try { unsubscribe && unsubscribe(); } catch (_) {} };
  }, [mapType, listClubFeatures]);

  // Consume pending hunt completion on mount for club maps
  useEffect(() => {
    if (mapType !== 'club') return;
    const pending = consumePendingHuntCompleted();
    if (pending) {
      (async () => {
        try { await listClubFeatures(); } catch (_) {}
      })();
    }
  }, [mapType, listClubFeatures]);

  // Detect presence of parcel params early (used by effects and callbacks)
  const hasParcelParams = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return ['parcel_bbox', 'parcel_ll', 'parcel_id', 'parcel_path', 'parcel_center'].some((k) => params.has(k));
    } catch (_) {
      return false;
    }
  }, [location.search]);

  const hasFeatureParam = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return params.has('featureId');
    } catch (_) {
      return false;
    }
  }, [location.search]);

  const hasStandParam = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return params.has('standId');
    } catch (_) {
      return false;
    }
  }, [location.search]);

  useEffect(() => {
    if (mapType === 'club') {
      setHighlightFeatureId(null);
      setSelectedFeatureId(null);
      try {
        const params = new URLSearchParams(location.search || '');
        const standParam = params.get('standId');
        if (standParam) {
          const parsed = Number(standParam);
          if (!Number.isNaN(parsed)) {
            setHighlightStandId(parsed);
          }
        }
      } catch (_) {}
      return;
    }

    setHighlightStandId(null);
    if (mapType !== 'personal') {
      setHighlightFeatureId(null);
      setSelectedFeatureId(null);
      return;
    }
    try {
      const params = new URLSearchParams(location.search || '');
      const featureParam = params.get('featureId');
      if (featureParam) {
        const parsed = Number(featureParam);
        if (!Number.isNaN(parsed)) {
          setHighlightFeatureId(parsed);
          setSelectedFeatureId(parsed);
        }
      }
    } catch (_) {}
  }, [location.search, mapType]);

  useEffect(() => {
    if (mapType === 'club' && hasStandParam) {
      markClubCameraReady();
    }
  }, [mapType, hasStandParam, markClubCameraReady]);

  // Handle features rendered
  const handleFeaturesRendered = useCallback((features) => {
    console.log('Features rendered on map:', features.length, 'features');
    const mapRef = mapInstanceRef.current;
    const initialStrategy = mapRef?.__yona_initialViewStrategy;
    const skipAutoFit = initialStrategy === 'saved' || initialStrategy === 'club' || initialStrategy === 'query' || initialStrategy === 'boundary';
    
    // If parcel params are present, re-assert focus (Chrome robustness)
    try {
      const map = mapInstanceRef.current;
      if (map) {
        const params = new URLSearchParams(location.search || '');
        const bboxStr = params.get('parcel_bbox');
        const ctrStr = params.get('parcel_center');
        if (bboxStr) {
          const parts = bboxStr.split(',').map(Number);
          if (parts.length === 4 && !parts.some((n) => Number.isNaN(n))) {
            const [w, s, e, n] = parts;
            const bounds = new mapboxgl.LngLatBounds([w, s], [e, n]);
            try { map.fitBounds(bounds, { padding: 50, maxZoom: 17, duration: 0 }); } catch (_) {}
          }
        } else if (ctrStr) {
          const arr = ctrStr.split(',').map(Number);
          if (arr.length === 2 && !arr.some((n) => Number.isNaN(n))) {
            try { map.easeTo({ center: arr, zoom: Math.max(15, (map.getZoom ? map.getZoom() : 15)), duration: 0 }); } catch (_) {}
          }
        }
      }
    } catch (_) {}
    
    // Update feature states after features are rendered
    if (mapType === 'club' && mapInstanceRef.current && features.length > 0 && !mapInstanceRef.current.__yona_isDrawing && !mapInstanceRef.current.__yona_isModalOpen && !mapInstanceRef.current.__yona_isPickerOpen) {
      console.log('Updating feature states after features rendered');
      updateFeatureStates(mapInstanceRef.current, features);
    }
    
    // Auto-fit to club boundaries after features are rendered
    if (!skipAutoFit && mapType === 'club' && mapInstanceRef.current && clubTracts(huntBoundaries).length > 0 && !hasFittedToClubBounds && !hasStandParam && !mapInstanceRef.current.__yona_isDrawing && !mapInstanceRef.current.__yona_isModalOpen && !mapInstanceRef.current.__yona_isPickerOpen) {
      console.log('Auto-fitting map to club boundaries after features rendered');
      
      const boundaryFeatures = clubTracts(huntBoundaries).map(boundary => ({
        type: 'Feature',
        geometry: boundary.geojson,
        properties: boundary.properties
      }));

      if (boundaryFeatures.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        boundaryFeatures.forEach(feature => {
          if (feature.geometry.coordinates) {
            if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates[0].forEach(coord => {
                bounds.extend(coord);
              });
            } else if (feature.geometry.type === 'MultiPolygon') {
              feature.geometry.coordinates.forEach(polygon => {
                polygon[0].forEach(coord => {
                  bounds.extend(coord);
                });
              });
            }
          }
        });

        try {
          mapInstanceRef.current.fitBounds(bounds, clubBoundaryFitBoundsOptions({ compact: isMobileMd }));
        } catch (_) {}

        setHasFittedToClubBounds(true);
        try { mapInstanceRef.current.__yona_initialViewStrategy = 'boundary'; } catch (_) {}
        markClubCameraReady();
      }
    }
    
    // Auto-fit to personal features after features are rendered
  if (!skipAutoFit && mapType === 'personal' && mapInstanceRef.current && features.length > 0 && !hasFittedToPersonalFeatures && !mapInstanceRef.current.__yona_isDrawing && !hasParcelParams && !hasFeatureParam) {
      console.log('Auto-fitting map to personal features after features rendered');
      
      const bounds = new mapboxgl.LngLatBounds();
      features.forEach(feature => {
        if (feature.geojson && feature.geojson.coordinates) {
          if (feature.geojson.type === 'Polygon') {
            feature.geojson.coordinates[0].forEach(coord => {
              bounds.extend(coord);
            });
          } else if (feature.geojson.type === 'MultiPolygon') {
            feature.geojson.coordinates.forEach(polygon => {
              polygon[0].forEach(coord => {
                bounds.extend(coord);
              });
            });
          } else if (feature.geojson.type === 'Point') {
            bounds.extend(feature.geojson.coordinates);
          } else if (feature.geojson.type === 'LineString') {
            feature.geojson.coordinates.forEach(coord => {
              bounds.extend(coord);
            });
          }
        }
      });

      try {
        const currentZoom = mapInstanceRef.current.getZoom();
        mapInstanceRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: Math.max(15, currentZoom)
        });
      } catch (_) {}
      
      setHasFittedToPersonalFeatures(true);
      try { mapInstanceRef.current.__yona_initialViewStrategy = 'features'; } catch (_) {}
    }
  }, [mapType, huntBoundaries, hasFittedToClubBounds, hasFittedToPersonalFeatures, hasStandParam, updateFeatureStates, hasParcelParams, hasFeatureParam, location.search, markClubCameraReady, isMobileMd]);

  // Reset auto-fit state when map type changes
  useEffect(() => {
    setHasFittedToClubBounds(false);
    setHasFittedToPersonalFeatures(false);
  }, [mapType]);

  // Calculate line distance utility function
  const calculateLineDistance = useCallback((coordinates) => {
    if (!coordinates || coordinates.length < 2) return 0;
    
    let totalDistance = 0;
    for (let i = 1; i < coordinates.length; i++) {
      const prev = coordinates[i - 1];
      const curr = coordinates[i];
      
      // Calculate distance between two points using Haversine formula
      const R = 6371000; // Earth's radius in meters
      const lat1 = prev[1] * Math.PI / 180;
      const lat2 = curr[1] * Math.PI / 180;
      const deltaLat = (curr[1] - prev[1]) * Math.PI / 180;
      const deltaLon = (curr[0] - prev[0]) * Math.PI / 180;
      
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      
      totalDistance += R * c;
    }
    
    // Convert to miles
    return totalDistance * 0.000621371;
  }, []);

  // Define club feature creation before draw handlers to avoid TDZ issues
  const handleClubFeatureCreate = useCallback((feature) => {
    console.log('Club feature creation triggered:', feature);
    
    // Set up club-specific draft state
    const geomType = feature.geometry.type.toLowerCase();
    setClubDraftFeature({
      tempId: feature.id,
      geometry: feature.geometry,
      type: geomType,
    });
    setClubDraftName(geomType === 'polygon' ? clubLandDrawDefaultName('boundary') : '');
    setClubDraftNotes('');
    setClubDraftIcon('default');
    setClubDraftRadius(0);
    setClubDraftLineColor('');
    setClubDraftFillColor('');
    setClubDraftFillOpacity(0);
    // Set default values for club-specific fields
    setClubDraftFeatureType('stand');
    setShowClubCreateModal(true);
  }, [setClubDraftFeature, setClubDraftName, setClubDraftNotes, setClubDraftIcon, setClubDraftRadius, setClubDraftLineColor, setClubDraftFillColor, setClubDraftFillOpacity, setClubDraftFeatureType, setShowClubCreateModal]);

  const openCreateWaypointModal = useCallback((drawn) => {
    setMarkerDraftFeature({ id: drawn.id, geometry: drawn.geometry, type: 'point' });
    setMarkerName('');
    setMarkerNotes('');
    setMarkerIsStand(true);
    setMarkerFeed(false);
    setMarkerType('');
    setMarkerWinds([]);
    setMarkerFeedingMode('');
    setMarkerFeedCapacityLbs(null);
    setMarkerConsumptionRateCode('');
    setMarkerRefillThresholdPct(20);
    setEditingMarkerId(null);
    featureListOpenBeforeEditorRef.current = Boolean(showFeatureList);
    setShowMarkerModal(true);
    try { drawInstanceRef.current && drawInstanceRef.current.delete(drawn.id); } catch {}
  }, [showFeatureList]);

  // Handle drawing events
  const handleDrawCreate = useCallback((e) => {
    const drawn = e.features && e.features[0];
    if (!drawn) return;
    if (clubDrawnOpensWaypointModal(drawn) && (mapType === 'personal' || mapType === 'club')) {
      openCreateWaypointModal(drawn);
      return;
    }
    if (mapType === 'personal') {
      handleFeatureCreate(drawn);
      try { drawInstanceRef.current && drawInstanceRef.current.delete(drawn.id); } catch {}
    } else if (mapType === 'club') {
      handleClubFeatureCreate(drawn);
      try { drawInstanceRef.current && drawInstanceRef.current.delete(drawn.id); } catch {}
    }
  }, [mapType, handleFeatureCreate, handleClubFeatureCreate, openCreateWaypointModal]);

  // Add club feature save handler
  const handleClubFeatureSave = useCallback(async () => {
    if (!clubDraftFeature) return;
    
    try {
      if (clubDraftFeature.type === 'polygon') {
        const clubMapId = await getClubMapId(clubId);
        const clusterGeoms = clubParcelClusters.length > 0
          ? clubParcelClusters
          : [clubDraftFeature.geometry];
        const names = clubTractNamesFromClusters(clusterGeoms, clubDraftName);
        for (let i = 0; i < clusterGeoms.length; i += 1) {
          const boundaryData = huntBoundaryCreatePayload({
            name: names[i] || clubDraftName || 'Boundary',
            geojson: clusterGeoms[i],
            notes: clubDraftNotes,
            clubMapId,
            isBoundary: clubDraftIsBoundary,
            lineColor: clubDraftLineColor,
            fillColor: clubDraftFillColor,
            fillOpacity: clubDraftFillOpacity,
          });
          await api.post('/hunt-boundaries/', boundaryData);
        }
        setClubParcelClusters([]);
        setHasFittedToClubBounds(false);
        clearClubViewExtent(mapInstanceRef.current);
        if (clubDraftIsBoundary !== false) {
          setClubConfirmingView(true);
        }
        
      } else if (clubDraftFeature.type === 'point') {
        const tract = parentHuntLocationToTract(clubDraftFeature.geometry, huntBoundaries);
        if (!tract) {
          try {
            show({
              title: 'Need a club boundary',
              body: 'Draw a tract before adding stands. Fields cannot hold markers.',
              variant: 'warning',
            });
          } catch (_) {
            alert('Draw a club boundary (tract) before adding stands.');
          }
          return;
        }
        const huntBoundaryId = tract.id;
        
        // Create hunt location with all required fields
        const locationData = {
          name: clubDraftName || 'Unnamed Location',
          geojson: clubDraftFeature.geometry,
          notes: clubDraftNotes,
          icon: clubDraftIcon,
          radius: clubDraftRadius > 0 ? clubDraftRadius : null,
          feature_type: clubDraftFeatureType, // Use the selected feature type
          optimal_winds: Array.isArray(clubDraftOptimalWinds) ? clubDraftOptimalWinds : [],
          hunt_boundary: huntBoundaryId // Use the actual boundary ID
        };
        
        console.log('Creating hunt location with data:', locationData);
        const res = await api.post('/hunt-locations/', locationData);
        console.log('Hunt location created:', res.data);
      } else if (clubDraftFeature.type === 'linestring') {
        // Create a club-shared personal GeoFeature line
        const payload = {
          type: 'line',
          geojson: clubDraftFeature.geometry,
          name: clubDraftName || 'Unnamed Line',
          notes: clubDraftNotes,
          share_type: 'club',
          shared_with_club_id: Number(clubId)
        };
        const res = await api.post('/features/', payload);
        console.log('Club-shared line feature created:', res.data);
      }
      
      // Refresh club features
      listClubFeatures();
      
      // Reset form
      setShowClubCreateModal(false);
      setClubDraftFeature(null);
      setClubDraftName('');
      setClubDraftNotes('');
      setClubDraftIcon('default');
      setClubDraftRadius(0);
      setClubDraftLineColor('');
      setClubDraftFillColor('');
      setClubDraftFillOpacity(0);
      setClubDraftFeatureType('stand');
      setClubDraftOptimalWinds([]);
      setClubDraftIsBoundary(true);
      setClubLandPath(null);
      
    } catch (e) {
      console.error('Club feature creation failed:', e);
      if (e.response?.status !== 401) {
        alert('Creation failed: ' + huntBoundarySaveError(e.response?.data, e.message));
      }
    }
  }, [clubDraftFeature, clubDraftName, clubDraftNotes, clubDraftIcon, clubDraftRadius, clubDraftFeatureType, clubDraftOptimalWinds, clubDraftLineColor, clubDraftFillColor, clubDraftFillOpacity, clubDraftIsBoundary, clubParcelClusters, clubId, listClubFeatures, getClubMapId, huntBoundaries, show]);

  const applyDrawDistanceUpdate = useCallback((e) => {
    try {
      const fromEvent = (() => {
        const feats = (e && e.features) || (e && e.drawn && e.drawn.features) || [];
        const f = Array.isArray(feats) ? feats.find(x => x?.geometry?.type === 'LineString') : null;
        return f && f.geometry && Array.isArray(f.geometry.coordinates) ? f.geometry.coordinates : null;
      })();

      let coords = fromEvent;
      if (!coords && drawInstanceRef.current && typeof drawInstanceRef.current.getAll === 'function') {
        const all = drawInstanceRef.current.getAll();
        const lf = all && Array.isArray(all.features)
          ? all.features.find(x => x?.geometry?.type === 'LineString' && Array.isArray(x.geometry.coordinates))
          : null;
        coords = lf ? lf.geometry.coordinates : null;
      }

      if (coords && coords.length >= 2) {
        setRealTimeDistance(calculateLineDistance(coords));
      } else {
        setRealTimeDistance(null);
      }
    } catch (_) {}
  }, [calculateLineDistance]);

  const handleDrawUpdate = useCallback((e) => {
    drawUpdateCoalescerRef.current.schedule(applyDrawDistanceUpdate, e);
  }, [applyDrawDistanceUpdate]);

  useEffect(() => () => {
    drawUpdateCoalescerRef.current?.cancel();
  }, []);

  const handleDrawDelete = useCallback((e) => {
    console.log('Feature deleted:', e);
    setRealTimeDistance(null);
  }, []);

  // Handle map ready
  const [mapReadyTick, setMapReadyTick] = useState(0);
  const handleMapReady = useCallback((map, draw) => {
    mapInstanceRef.current = map;
    drawInstanceRef.current = draw;
    setMapReadyTick((t) => t + 1);
    console.log('Map ready in UnifiedMapPage');
  }, []);

  const openTagOutPicker = useCallback(() => {
    const firstStand = clubStands[0];
    if (firstStand) {
      setPickerFocusedStandId(firstStand.id);
      setPickerSelectedStandId(firstStand.id);
    }
    setShowTagOutPicker(true);
  }, [clubStands]);

  const closeTagOutPicker = useCallback(() => {
    setShowTagOutPicker(false);
    setPickerFocusedStandId(null);
    setPickerSelectedStandId(null);
    setPickerSheetHeight(0);
    clearMapPadding(mapInstanceRef.current);
  }, []);

  const handleTagOutFromPicker = useCallback(() => {
    const stand = clubStands.find((row) => Number(row.id) === Number(pickerSelectedStandId));
    if (!stand || stand.isOccupied) return;

    const feature = buildClubStandFeature(stand);
    setShowTagOutPicker(false);
    setPickerFocusedStandId(null);
    setPickerSelectedStandId(null);
    setPickerSheetHeight(0);
    clearMapPadding(mapInstanceRef.current);

    try {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.__yona_isModalOpen = true;
        mapInstanceRef.current.__yona_isPickerOpen = false;
      }
    } catch (_) {}

    setSheetOccupancy(null);
    setSelectedLocation(feature);
    setShowSignOutModal(true);
  }, [clubStands, pickerSelectedStandId]);

  const openOccupiedStandSheet = useCallback((standRow, occupancy) => {
    const sheetLocation = buildClubStandFeature(standRow);
    try {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.__yona_isModalOpen = true;
      }
    } catch (_) {}
    setSheetOccupancy(occupancy);
    setSelectedLocation(sheetLocation);
    setShowSignOutModal(true);
  }, []);

  const openStandFromBoard = useCallback((standRow) => {
    if (!standRow) return;
    setBoardSelectedStandId(standRow.id);
    const feature = buildClubStandFeature(standRow);
    if (standRow.isOccupied) {
      const occupancy = buildOccupancyFromStandRow(
        standRow,
        currentUser?.id,
        toLocalYMD(new Date())
      );
      openOccupiedStandSheet(standRow, occupancy);
      return;
    }
    try {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.__yona_isModalOpen = true;
      }
    } catch (_) {}
    setSheetOccupancy(null);
    setSelectedLocation(feature);
    setShowSignOutModal(true);
  }, [currentUser?.id, openOccupiedStandSheet]);

  const handleViewTaggedFromPicker = useCallback((stand) => {
    if (!stand) return;
    const occupancy = buildOccupancyFromStandRow(
      stand,
      currentUser?.id,
      toLocalYMD(new Date())
    );
    openOccupiedStandSheet(stand, occupancy);
  }, [currentUser?.id, openOccupiedStandSheet]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    const shouldLock = showTagOutPicker || showSignOutModal;
    try { map.__yona_isPickerOpen = showTagOutPicker; } catch (_) {}

    const interactionKeys = ['dragPan', 'touchZoomRotate', 'scrollZoom', 'boxZoom', 'doubleClickZoom'];
    interactionKeys.forEach((key) => {
      try {
        if (shouldLock) map[key]?.disable();
        else map[key]?.enable();
      } catch (_) {}
    });

    return () => {
      if (!showSignOutModal) {
        interactionKeys.forEach((key) => {
          try { map[key]?.enable(); } catch (_) {}
        });
        try { map.__yona_isPickerOpen = false; } catch (_) {}
      }
    };
  }, [showTagOutPicker, showSignOutModal, mapReadyTick]);

  // Custom tool handlers
  const startDropWaypoint = useCallback(() => {
    if (!drawInstanceRef.current) return;
    try {
      if (mapInstanceRef.current) mapInstanceRef.current.__yona_creationContext = 'point';
    } catch (_) {}
    try { drawInstanceRef.current.changeMode('draw_point'); } catch {}
    try { mapInstanceRef.current?.__yona_syncPlacementToolChrome?.(); } catch (_) {}
  }, []);

  const chooseClubLandPath = useCallback((path) => {
    setClubLandPath(path);
    setClubDraftIsBoundary(true);
    if (path === 'draw') {
      try { drawInstanceRef.current && drawInstanceRef.current.changeMode('draw_polygon'); } catch (_) {}
      return;
    }
    if (path === 'parcels') {
      if (canEnableParcels !== true) {
        setShowParcelsPro(true);
        setClubLandPath(null);
        return;
      }
      toggleParcelSelectMode(true);
    }
  }, [canEnableParcels, toggleParcelSelectMode]);

  const cancelMarkerModal = useCallback(() => {
    setShowMarkerModal(false);
    setMarkerDraftFeature(null);
    setMarkerName('');
    setMarkerNotes('');
    setMarkerIsStand(true);
    setMarkerFeed(false);
    setMarkerType('');
    setMarkerWinds([]);
    setMarkerFeedingMode('');
    setMarkerFeedCapacityLbs(null);
    setMarkerConsumptionRateCode('');
    setMarkerRefillThresholdPct(20);
    try {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.__yona_creationContext = null;
        mapInstanceRef.current.__yona_syncPlacementToolChrome?.();
      }
    } catch (_) {}
    setEditingMarkerId(null);
    const wasOpen = featureListOpenBeforeEditorRef.current;
    featureListOpenBeforeEditorRef.current = false;
    restoreFeatureListIfNeeded(wasOpen, onShowFeatureList);
  }, [onShowFeatureList]);

  const saveMarker = useCallback(async () => {
    if (!markerDraftFeature || markerDraftFeature.type !== 'point') return;
    let closeModal = true;
    try {
      if (mapType === 'club') {
        const tract = parentHuntLocationToTract(markerDraftFeature.geometry, huntBoundaries);
        if (!tract) {
          closeModal = false;
          try {
            show({
              title: 'Need a club boundary',
              body: 'Draw a tract before adding waypoints. Fields cannot hold markers.',
              variant: 'warning',
            });
          } catch (_) {
            alert('Draw a club boundary (tract) before adding waypoints.');
          }
          return;
        }
        const payload = buildClubHuntLocationPayload({
          tractId: tract.id,
          geometry: markerDraftFeature.geometry,
          name: markerName,
          notes: markerNotes,
          isStand: markerIsStand,
          feed: markerFeed,
          winds: markerWinds,
          feedingMode: markerFeedingMode,
          feedCapacityLbs: markerFeedCapacityLbs,
          consumptionRateCode: markerConsumptionRateCode,
          refillThresholdPct: markerRefillThresholdPct
        });
        if (editingMarkerId) {
          await api.patch(`/hunt-locations/${editingMarkerId}/`, payload);
          try { show({ title: 'Waypoint updated', body: 'Stand and feed settings saved.', variant: 'success' }); } catch(_) {}
        } else {
          await api.post('/hunt-locations/', payload);
          try { show({ title: 'Waypoint created', body: 'Stand and feed settings saved.', variant: 'success' }); } catch(_) {}
        }
        await listClubFeatures();
      } else {
        const identity = buildPointIdentityPayload({
          isStand: markerIsStand,
          feed: markerFeed,
          markerType,
          winds: markerWinds,
          feedingMode: markerFeedingMode,
          feedCapacityLbs: markerFeedCapacityLbs,
          consumptionRateCode: markerConsumptionRateCode,
          refillThresholdPct: markerRefillThresholdPct
        });
        if (editingMarkerId) {
          const payload = {
            name: markerName || 'Unnamed',
            notes: markerNotes,
            ...identity
          };
          await api.patch(`/features/${editingMarkerId}/`, payload);
          try { show({ title: 'Waypoint updated', body: 'Stand and feed settings saved.', variant: 'success' }); } catch(_) {}
        } else {
          const payload = {
            type: 'point',
            geojson: markerDraftFeature.geometry,
            name: markerName || 'Unnamed',
            notes: markerNotes,
            share: false,
            icon: 'default',
            radius: null,
            ...identity
          };
          await api.post('/features/', payload);
          try { show({ title: 'Waypoint created', body: 'Stand and feed settings saved.', variant: 'success' }); } catch(_) {}
        }
        await listFeatures();
      }
    } catch (e) {
      console.error('Marker save failed', e);
      const isEdit = Boolean(editingMarkerId);
      const msg = (e && e.response && (e.response.data?.error || JSON.stringify(e.response.data))) || e.message || 'Request failed';
      if (e.response?.status !== 401) alert(`${isEdit ? 'Waypoint update failed' : 'Waypoint creation failed'}: ${msg}`);
    } finally {
      if (closeModal) cancelMarkerModal();
    }
  }, [
    markerDraftFeature,
    markerName,
    markerNotes,
    markerIsStand,
    markerFeed,
    markerType,
    markerWinds,
    editingMarkerId,
    listFeatures,
    listClubFeatures,
    cancelMarkerModal,
    markerFeedingMode,
    markerFeedCapacityLbs,
    markerConsumptionRateCode,
    markerRefillThresholdPct,
    mapType,
    huntBoundaries,
    show
  ]);

  // Personal and club points use the same waypoint identity UI as create.
  const openWaypointEditor = useCallback((feature, editorId) => {
    const form = waypointFormFromFeature(feature);
    const id = editorId ?? form.id;
    setEditingMarkerId(id);
    setMarkerDraftFeature({ id, geometry: form.geometry, type: 'point' });
    setMarkerName(form.name);
    setMarkerNotes(form.notes);
    setMarkerIsStand(form.isStand);
    setMarkerFeed(form.feed);
    setMarkerType(form.markerType);
    setMarkerWinds(form.winds);
    setMarkerFeedingMode(form.feedingMode);
    setMarkerFeedCapacityLbs(form.feedCapacityLbs);
    setMarkerConsumptionRateCode(form.consumptionRateCode);
    setMarkerRefillThresholdPct(form.refillThresholdPct);
    featureListOpenBeforeEditorRef.current = Boolean(showFeatureList);
    if (typeof onCloseFeatureList === 'function') onCloseFeatureList();
    setShowMarkerModal(true);
  }, [onCloseFeatureList, showFeatureList]);

  const handlePersonalEdit = useCallback((feature) => {
    if (shouldEditAsWaypoint(feature)) {
      openWaypointEditor(feature);
      return;
    }
    if (typeof onCloseFeatureList === 'function') onCloseFeatureList();
    handleEditFeature(feature);
  }, [handleEditFeature, onCloseFeatureList, openWaypointEditor]);

  const handleToggleHidden = useCallback((feature) => {
    setHiddenFeatureIds((prev) => {
      const next = new Set(prev);
      const fid = String(feature.id);
      if (next.has(fid)) next.delete(fid);
      else next.add(fid);
      return next;
    });
  }, []);

  const handleStartGeometryEdit = useCallback((feature) => {
    try {
      if (!drawInstanceRef.current || !feature?.geojson) return;
      ensureMapMaximized({ mapMaximized, onToggleMapMaximize });
      if (mapInstanceRef.current) {
        const geometry = feature.geojson;
        if (geometry?.type === 'Point') {
          mapInstanceRef.current.flyTo({ center: geometry.coordinates, zoom: 16, duration: 1000 });
        } else if (geometry?.type === 'Polygon') {
          const bounds = new mapboxgl.LngLatBounds();
          try { geometry.coordinates[0].forEach((coord) => bounds.extend(coord)); } catch (_) {}
          if (!bounds.isEmpty()) {
            mapInstanceRef.current.fitBounds(bounds, { padding: 50, maxZoom: 16, duration: 1000 });
          }
        } else if (geometry?.type === 'LineString') {
          const bounds = new mapboxgl.LngLatBounds();
          try { geometry.coordinates.forEach((coord) => bounds.extend(coord)); } catch (_) {}
          if (!bounds.isEmpty()) {
            mapInstanceRef.current.fitBounds(bounds, { padding: 50, maxZoom: 16, duration: 1000 });
          }
        }
      }
      const temp = {
        id: `edit-${feature.id}`,
        type: 'Feature',
        geometry: feature.geojson,
        properties: {},
      };
      const addedId = drawInstanceRef.current.add(temp);
      const useId = Array.isArray(addedId) ? addedId[0] : addedId;
      setDrawFeatureId(useId);
      const geomType = feature?.geojson?.type;
      if (geomType === 'Point') {
        drawInstanceRef.current.changeMode('simple_select', { featureIds: [useId] });
      } else {
        drawInstanceRef.current.changeMode('direct_select', { featureId: useId });
      }
      setIsGeometryEditing(true);
      if (mapType === 'club') {
        setClubEditingFeature(feature);
      } else {
        setGeometryEditingFeature(feature);
      }
      if (typeof onCloseFeatureList === 'function') onCloseFeatureList();
    } catch (_) {}
  }, [mapMaximized, mapType, onCloseFeatureList, onToggleMapMaximize]);

  // Add map move handler for weather widget
  const handleMapMove = useCallback((mapInfo) => {
    if (mapInfo && mapInfo.center) {
      setMapCenter({
        lat: mapInfo.center.lat,
        lng: mapInfo.center.lng
      });
    }
  }, []);

  // Fetch current wind for map center periodically (simple: on center change)
  useEffect(() => {
    let ignore = false;
    async function loadWind() {
      try {
        const wx = await weatherService.getCurrentWeather(mapCenter.lat, mapCenter.lng, currentUser?.id);
        if (!ignore && wx && typeof wx.wind_direction === 'number') {
          setCurrentWindDeg(wx.wind_direction);
        }
      } catch (e) {
        // silent fail
      }
    }
    loadWind();
    return () => { ignore = true; };
  }, [mapCenter.lat, mapCenter.lng, currentUser?.id]);

  // Handle feature click with hunt sign-out logic
  const handleFeatureClick = useCallback(async (feature) => {
    console.log('=== FEATURE CLICK DEBUG ===');
    console.log('Feature clicked:', feature);
    console.log('Map type:', mapType);
    console.log('Feature type:', feature.geojson?.type);
    console.log('Feature properties:', feature.properties);

    if (mapType === 'personal') {
      const nextId = selectedMapFeatureId(feature);
      if (nextId != null) setSelectedFeatureId(nextId);
    }
    
    if (!mapInstanceRef.current) {
      console.log('No map instance available');
      return;
    }

    // Stop any in-flight camera animations immediately to prevent incidental zoom/pan
    try { mapInstanceRef.current.stop && mapInstanceRef.current.stop(); } catch (_) {}

// ... existing code ...

// Handle hunt sign-out for club maps
if (mapType === 'club') {
  // Check if this is a hunt location point (robust checks)
  const p = feature.properties || {};
  const isHuntLocation = (
    feature.featureType === 'hunt_location' ||
    p.featureType === 'hunt_location' ||
    p.feature_type === 'hunt_location' ||
    p.isHuntLocation === true
  );
  
  if (isHuntLocation) {
    console.log('Club map hunt location clicked - checking sign-out status');
    
    // Use the isOccupied property from the feature data
    const isOccupied = feature.isOccupied || feature.properties?.isOccupied;
    console.log('Feature ID:', feature.id, 'Properties ID:', feature.properties?.id, 'Is occupied:', isOccupied);
    
    if (isOccupied) {
      console.log('Location is occupied - opening stand sheet');
      const featureId = feature.properties?.id || feature.id || feature.properties?.locationId;
      const locationId = featureId ? String(featureId).replace('location-', '') : null;
      if (!locationId) {
        console.error('Could not determine location ID from feature:', feature);
        return;
      }
      try {
        const response = await api.get(`/hunt-locations/${locationId}/`);
        const locationData = response.data;
        const { toLocalYMD: toYmd } = await import('../../../utils/dateUtils');
        const today = toYmd(new Date());
        const signOutDetails = locationData.sign_out_records?.find(
          (record) => record.date === today
        );
        if (!signOutDetails) return;

        const occupancy = buildOccupancyFromSignOut(
          signOutDetails,
          currentUser?.id,
          today
        );
        try {
          if (mapInstanceRef.current) {
            try { mapInstanceRef.current.stop && mapInstanceRef.current.stop(); } catch (_) {}
            mapInstanceRef.current.__yona_isModalOpen = true;
          }
        } catch (_) {}
        setSheetOccupancy(occupancy);
        setSelectedLocation({
          ...feature,
          name: locationData.name || feature.name,
          notes: locationData.notes || '',
          optimal_wind: locationData.optimal_wind,
          optimal_winds: locationData.optimal_winds,
          geojson: locationData.geojson || feature.geojson,
          location: locationData,
        });
        setShowSignOutModal(true);
      } catch (error) {
        console.error('Failed to get location data for occupied stand sheet:', error);
      }
    } else {
      // Location is available - show sign-out modal
      console.log('Location is available - showing sign-out modal');
      try {
        if (mapInstanceRef.current) {
          // Ensure any pending animation is stopped before opening modal
          try { mapInstanceRef.current.stop && mapInstanceRef.current.stop(); } catch(_) {}
          mapInstanceRef.current.__yona_isModalOpen = true;
          // Optionally pause interactions to reduce jitter perception
          try { mapInstanceRef.current.scrollZoom && mapInstanceRef.current.scrollZoom.disable(); } catch(_) {}
          try { mapInstanceRef.current.boxZoom && mapInstanceRef.current.boxZoom.disable(); } catch(_) {}
          try { mapInstanceRef.current.dragPan && mapInstanceRef.current.dragPan.disable(); } catch(_) {}
          try { mapInstanceRef.current.keyboard && mapInstanceRef.current.keyboard.disable(); } catch(_) {}
          try { mapInstanceRef.current.doubleClickZoom && mapInstanceRef.current.doubleClickZoom.disable(); } catch(_) {}
        }
      } catch(_) {}
      const locationId = resolveHuntLocationNumericId(feature);
      const stand = locationId
        ? clubStands.find((row) => Number(row.id) === Number(locationId))
        : null;
      setSheetOccupancy(null);
      setSelectedLocation(stand ? buildClubStandFeature(stand) : feature);
      setShowSignOutModal(true);
    }
    return;
  }
}

// ... rest of the function ...

    // If a modal is open, never change camera
    if (mapInstanceRef.current && mapInstanceRef.current.__yona_isModalOpen) {
      return;
    }

    // Regular feature click (zoom to feature)
    console.log('Regular feature click - zooming to feature');
    
    if (feature.geojson) {
      const geometry = feature.geojson;
      console.log('Geometry found:', geometry);
      
      if (geometry.type === 'Point') {
        console.log('Focusing on point feature');
        mapInstanceRef.current.flyTo({
          center: geometry.coordinates,
          zoom: 16,
          duration: 2000
        });
      } else if (geometry.type === 'Polygon') {
        console.log('Focusing on polygon feature');
        const bounds = new mapboxgl.LngLatBounds();
        geometry.coordinates[0].forEach(coord => {
          bounds.extend(coord);
        });
        mapInstanceRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      } else if (geometry.type === 'LineString') {
        console.log('Focusing on line feature');
        const bounds = new mapboxgl.LngLatBounds();
        geometry.coordinates.forEach(coord => {
          bounds.extend(coord);
        });
        mapInstanceRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    } else if (feature.geometry) {
      // Handle different feature structure
      const geometry = feature.geometry;
      console.log('Geometry found (alternative structure):', geometry);
      
      if (geometry.type === 'Point') {
        console.log('Focusing on point feature (alternative structure)');
        mapInstanceRef.current.flyTo({
          center: geometry.coordinates,
          zoom: 16,
          duration: 2000
        });
      } else if (geometry.type === 'Polygon') {
        console.log('Focusing on polygon feature (alternative structure)');
        const bounds = new mapboxgl.LngLatBounds();
        geometry.coordinates[0].forEach(coord => {
          bounds.extend(coord);
        });
        mapInstanceRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      } else if (geometry.type === 'LineString') {
        console.log('Focusing on line feature (alternative structure)');
        const bounds = new mapboxgl.LngLatBounds();
        geometry.coordinates.forEach(coord => {
          bounds.extend(coord);
        });
        mapInstanceRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    }
  }, [mapType, currentUser?.id, clubStands, setShowSignOutModal, setSelectedLocation]);

  // Handle sign-out
  const handleSignOut = useCallback(async (locationId, userId, date, timePeriod) => {
    try {
      await signOutLocation(locationId, userId, date, timePeriod);
      
      // Refresh features to get updated sign-out data from server
      if (mapType === 'club') {
        console.log('Refreshing club features after sign-out');
        await listClubFeatures();
      }
      
      setShowSignOutModal(false);
      setSelectedLocation(null);
      setSheetOccupancy(null);
      try { notifyInTheStandChanged(); } catch (_) {}
      try { show({ title: 'Signed out to hunt', body: 'You are signed out for this time period.', variant: 'success' }); } catch (e) {}
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }, [signOutLocation, mapType, listClubFeatures, show]);

  const handleSignIn = useCallback(async (locationId, date, timePeriod, options = {}) => {
    const { refresh = true, userId = null, requireRemoved = false } = options;
    await signInLocation(locationId, date, timePeriod, { userId, requireRemoved });
    try { notifyInTheStandChanged(); } catch (_) {}
    if (refresh === false) return;
    if (mapType === 'club') {
      await listClubFeatures();
    }
  }, [signInLocation, mapType, listClubFeatures]);

  const handleStandReleased = useCallback(() => {
    try {
      show({ title: 'Stand cleared', body: 'The stand is open again.', variant: 'success' });
    } catch (e) {}
  }, [show]);

  // Handle sign-out modal close
  const handleSignOutModalClose = useCallback(() => {
    setShowSignOutModal(false);
    setSelectedLocation(null);
    setSheetOccupancy(null);
    clearSignOutError();
    // Re-enable interactions and clear modal guard
    try {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.__yona_isModalOpen = false;
        try { mapInstanceRef.current.scrollZoom && mapInstanceRef.current.scrollZoom.enable(); } catch(_) {}
        try { mapInstanceRef.current.boxZoom && mapInstanceRef.current.boxZoom.enable(); } catch(_) {}
        try { mapInstanceRef.current.dragPan && mapInstanceRef.current.dragPan.enable(); } catch(_) {}
        try { mapInstanceRef.current.keyboard && mapInstanceRef.current.keyboard.enable(); } catch(_) {}
        try { mapInstanceRef.current.doubleClickZoom && mapInstanceRef.current.doubleClickZoom.enable(); } catch(_) {}
      }
    } catch(_) {}
  }, [clearSignOutError]);

  // Debug logging
  useEffect(() => {
    console.log('=== UNIFIED MAP PAGE DEBUG ===');
    console.log('showFeatureList:', showFeatureList);
    console.log('allFeatures:', allFeatures);
    console.log('handleFeatureClick:', handleFeatureClick);
    console.log('handleEditFeature:', handleEditFeature);
    console.log('handleDeleteRequest:', handleDeleteRequest);
    console.log('mapInstanceRef.current:', mapInstanceRef.current);
  }, [showFeatureList, allFeatures, handleFeatureClick, handleEditFeature, handleDeleteRequest]);

  // --- Club edit/delete handlers (president only) ---
  const [clubEditingFeature, setClubEditingFeature] = useState(null);
  const [clubEditName, setClubEditName] = useState('');
  const [clubEditNotes, setClubEditNotes] = useState('');
  const [clubEditRadius, setClubEditRadius] = useState(0);
  const [clubEditLineColor, setClubEditLineColor] = useState('');
  const [clubEditFillColor, setClubEditFillColor] = useState('');
  const [clubEditFillOpacity, setClubEditFillOpacity] = useState(0);
  const [showClubEditModal, setShowClubEditModal] = useState(false);
  const [isGeometryEditing, setIsGeometryEditing] = useState(false);
  const [drawFeatureId, setDrawFeatureId] = useState(null);
  const [clubEditFeatureType, setClubEditFeatureType] = useState('stand');
  const [clubEditOptimalWind, setClubEditOptimalWind] = useState('');
  const [clubEditOptimalWinds, setClubEditOptimalWinds] = useState([]);
  const [clubEditIsBoundary, setClubEditIsBoundary] = useState(true);

  const beginClubEdit = useCallback((feature) => {
    if (clubPointUsesWaypointEditor(feature)) {
      const numericId = clubHuntLocationNumericId(feature);
      if (numericId != null) {
        openWaypointEditor(feature, numericId);
        return;
      }
    }
    setClubEditingFeature(feature);
    setClubEditName(feature.name || '');
    setClubEditNotes(feature.notes || '');
    setClubEditRadius(feature.radius || 0);
    setClubEditLineColor(feature.line_color || feature.lineColor || '');
    setClubEditFillColor(feature.fill_color || feature.fillColor || '');
    setClubEditFillOpacity(typeof feature.fill_opacity === 'number' ? feature.fill_opacity : (typeof feature.fillOpacity === 'number' ? feature.fillOpacity : 0));
    setClubEditFeatureType(feature.feature_type || 'stand');
    setClubEditOptimalWind(feature.optimal_wind || '');
    setClubEditOptimalWinds(feature.optimal_winds || []);
    setClubEditIsBoundary(isHuntTract(feature));
    featureListOpenBeforeEditorRef.current = Boolean(showFeatureList);
    if (typeof onCloseFeatureList === 'function') onCloseFeatureList();
    setShowClubEditModal(true);
  }, [onCloseFeatureList, openWaypointEditor, showFeatureList]);

  const editClubBoardWaypoint = useCallback((location) => {
    const feature = clubBoardLocationAsEditFeature(location);
    if (!feature) return;
    beginClubEdit(feature);
  }, [beginClubEdit]);

  const moveClubBoardWaypoint = useCallback((location) => {
    const feature = clubBoardLocationAsEditFeature(location);
    if (!feature) return;
    handleStartGeometryEdit(feature);
  }, [handleStartGeometryEdit]);

  const startClubWaypointMoveFromEditor = useCallback(() => {
    if (editingMarkerId == null || !markerDraftFeature?.geometry) return;
    handleStartGeometryEdit({
      id: `location-${editingMarkerId}`,
      featureType: 'hunt_location',
      type: 'point',
      geojson: markerDraftFeature.geometry,
    });
    setShowMarkerModal(false);
  }, [editingMarkerId, markerDraftFeature, handleStartGeometryEdit]);

  const requestClubWaypointDelete = useCallback((location) => {
    const plan = clubWaypointDeletePlan(location, {
      stands: clubStands,
      locations: huntLocations,
    });
    if (!plan) return;
    setShowMarkerModal(false);
    setClubWaypointDeleteTarget({ location, plan });
  }, [clubStands, huntLocations]);

  const startClubWaypointDeleteFromEditor = useCallback(() => {
    if (editingMarkerId == null) return;
    requestClubWaypointDelete({ id: editingMarkerId });
  }, [editingMarkerId, requestClubWaypointDelete]);

  const cancelClubWaypointDelete = useCallback(() => {
    if (clubWaypointDeleteBusy) return;
    setClubWaypointDeleteTarget(null);
  }, [clubWaypointDeleteBusy]);

  const confirmClubWaypointDelete = useCallback(async () => {
    if (!clubWaypointDeleteTarget?.plan?.id || clubWaypointDeleteBusy) return;
    setClubWaypointDeleteBusy(true);
    try {
      const feature = clubBoardLocationAsEditFeature({ id: clubWaypointDeleteTarget.plan.id });
      const { path } = mapClubFeatureToEndpoint(feature);
      await api.delete(path);
      await listClubFeatures();
      setClubWaypointDeleteTarget(null);
      try {
        show({
          title: 'Waypoint deleted',
          body: 'The pin was removed from the club map.',
          variant: 'info',
        });
      } catch (_) {}
    } catch (e) {
      if (e.response?.status === 401) return;
      const body = huntBoundarySaveError(
        e.response?.data,
        'Could not delete this waypoint.'
      );
      try {
        show({
          title: 'Could not delete',
          body,
          variant: 'warning',
        });
      } catch (_) {
        alert(body);
      }
    } finally {
      setClubWaypointDeleteBusy(false);
    }
  }, [clubWaypointDeleteBusy, clubWaypointDeleteTarget, listClubFeatures, show]);

  const focusClubAreaFromBoard = useCallback((area) => {
    if (!area) return;
    setBoardSelectedAreaId(area.id);
    const map = mapInstanceRef.current;
    const bbox = bboxFromAreaGeojson(area.geojson);
    if (!map || !bbox) return;
    try { map.stop && map.stop(); } catch (_) {}
    try {
      map.fitBounds(
        [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
        { padding: 64, maxZoom: 16, duration: 500 }
      );
    } catch (_) {}
  }, []);

  const editClubBoardArea = useCallback((area) => {
    const feature = clubBoardBoundaryAsEditFeature(area);
    if (!feature) return;
    beginClubEdit(feature);
  }, [beginClubEdit]);

  const editClubBoardAreaShape = useCallback((area) => {
    const feature = clubBoardBoundaryAsEditFeature(area);
    if (!feature) return;
    handleStartGeometryEdit(feature);
  }, [handleStartGeometryEdit]);

  const startClubShapeFromEditor = useCallback(() => {
    if (!clubEditingFeature) return;
    setShowClubEditModal(false);
    handleStartGeometryEdit(clubEditingFeature);
  }, [clubEditingFeature, handleStartGeometryEdit]);

  const requestClubLandDelete = useCallback((area) => {
    const feature = area?.featureType === 'hunt_boundary'
      ? area
      : clubBoardBoundaryAsEditFeature(area);
    if (!feature) return;
    const plan = clubLandDeletePlan(feature, {
      boundaries: huntBoundaries,
      locations: huntLocations,
    });
    if (!plan) return;
    if (plan.blocked) {
      try {
        show({
          title: plan.title || 'Delete stands first',
          body: plan.body,
          variant: 'warning',
        });
      } catch (_) {
        alert(plan.body);
      }
      return;
    }
    setShowClubEditModal(false);
    setClubLandDeleteTarget({ feature, plan });
  }, [huntBoundaries, huntLocations, show]);

  const cancelClubLandDelete = useCallback(() => {
    if (clubLandDeleteBusy) return;
    setClubLandDeleteTarget(null);
  }, [clubLandDeleteBusy]);

  const confirmClubLandDelete = useCallback(async () => {
    if (!clubLandDeleteTarget?.feature || clubLandDeleteBusy) return;
    setClubLandDeleteBusy(true);
    try {
      const { path } = mapClubFeatureToEndpoint(clubLandDeleteTarget.feature);
      await api.delete(path, { data: clubLandDeletePayload() });
      await listClubFeatures();
      setClubLandDeleteTarget(null);
      try {
        show({
          title: 'Land deleted',
          body: clubLandDeleteTarget.feature.name
            ? `"${clubLandDeleteTarget.feature.name}" was removed.`
            : 'The polygon was removed.',
          variant: 'info',
        });
      } catch (_) {}
    } catch (e) {
      if (e.response?.status === 401) return;
      const body = huntBoundarySaveError(
        e.response?.data,
        'Could not delete this club land.'
      );
      try {
        show({
          title: 'Could not delete',
          body,
          variant: 'warning',
        });
      } catch (_) {
        alert(body);
      }
    } finally {
      setClubLandDeleteBusy(false);
    }
  }, [clubLandDeleteBusy, clubLandDeleteTarget, listClubFeatures, show]);

  const cancelClubEdit = useCallback(() => {
    setShowClubEditModal(false);
    setClubEditingFeature(null);
    setClubEditName('');
    setClubEditNotes('');
    setClubEditRadius(0);
    setClubEditLineColor('');
    setClubEditFillColor('');
    setClubEditFillOpacity(0);
    setClubEditFeatureType('stand');
    setClubEditOptimalWind('');
    setClubEditOptimalWinds([]);
    setClubEditIsBoundary(true);
    // Cleanup draw if needed
    if (isGeometryEditing && drawInstanceRef.current && drawFeatureId) {
      try { drawInstanceRef.current.delete(drawFeatureId); } catch {}
    }
    setIsGeometryEditing(false);
    setDrawFeatureId(null);
    const wasOpen = featureListOpenBeforeEditorRef.current;
    featureListOpenBeforeEditorRef.current = false;
    restoreFeatureListIfNeeded(wasOpen, onShowFeatureList);
  }, [isGeometryEditing, drawFeatureId, onShowFeatureList]);

  const saveClubEdit = useCallback(async () => {
    if (!clubEditingFeature) return;
    try {
      const { path, kind } = mapClubFeatureToEndpoint(clubEditingFeature);
      const payload = { name: clubEditName || 'Unnamed', notes: clubEditNotes };
      if (kind === 'location') {
        payload.radius = clubEditRadius > 0 ? clubEditRadius : null;
        payload.feature_type = clubEditFeatureType || 'stand';
        // Prefer multi-wind array; send single only if no array selected
        if (clubEditOptimalWinds && clubEditOptimalWinds.length > 0) {
          payload.optimal_winds = clubEditOptimalWinds;
        } else {
          payload.optimal_wind = clubEditOptimalWind || null;
        }
      } else if (kind === 'boundary') {
        // Forward optional styling for boundaries
        if (clubEditLineColor) payload.line_color = clubEditLineColor;
        if (clubEditFillColor) payload.fill_color = clubEditFillColor;
        if (typeof clubEditFillOpacity === 'number') payload.fill_opacity = clubEditFillOpacity;
        payload.is_boundary = clubEditIsBoundary;
      }
      await api.patch(path, payload);
      await listClubFeatures();
      cancelClubEdit();
    } catch (e) {
      console.error('Club edit failed', e);
      if (e.response?.status === 401) return;
      const roleError = huntBoundaryRoleError(e.response?.data);
      try {
        show({
          title: roleError ? 'Move stands first' : 'Update failed',
          body: roleError || 'Could not save this feature.',
          variant: 'warning',
        });
      } catch (_) {
        alert(roleError || 'Update failed');
      }
    }
  }, [clubEditingFeature, clubEditName, clubEditNotes, clubEditRadius, clubEditFeatureType, clubEditOptimalWind, clubEditOptimalWinds, clubEditLineColor, clubEditFillColor, clubEditFillOpacity, clubEditIsBoundary, listClubFeatures, cancelClubEdit, show]);

  const saveGeometryEdit = useCallback(async () => {
    if (!drawInstanceRef.current || !drawFeatureId) return;
    try {
      const edited = drawInstanceRef.current.get(drawFeatureId);
      if (!edited || !edited.geometry) return;
      // Determine correct endpoint based on context (club vs personal)
      if (mapType === 'club' && clubEditingFeature) {
        const { path, kind } = mapClubFeatureToEndpoint(clubEditingFeature);
        if (kind === 'location') {
          const payload = clubWaypointMovePayload(edited.geometry, huntBoundaries);
          if (!payload) {
            try {
              show({
                title: 'Need a club boundary',
                body: 'Draw a tract before moving waypoints. Fields cannot hold markers.',
                variant: 'warning',
              });
            } catch (_) {
              alert('Draw a club boundary (tract) before moving waypoints.');
            }
            return;
          }
          await api.patch(path, payload);
        } else {
          await api.patch(path, { geojson: edited.geometry });
        }
      } else if (geometryEditingFeature) {
        await api.patch(`/features/${geometryEditingFeature.id}/`, { geojson: edited.geometry });
      }
      if (mapType === 'club') {
        await listClubFeatures();
      } else {
        await listFeatures();
      }
      // Cleanup
      try { drawInstanceRef.current.delete(drawFeatureId); } catch {}
      setIsGeometryEditing(false);
      setDrawFeatureId(null);
      if (mapType === 'club') {
        setShowClubEditModal(false);
        setClubEditingFeature(null);
      }
    } catch (e) {
      console.error('Failed to save geometry', e);
      if (e.response?.status !== 401) alert('Saving geometry failed');
    }
  }, [clubEditingFeature, geometryEditingFeature, drawFeatureId, huntBoundaries, listClubFeatures, listFeatures, mapType, show]);

  const cancelGeometryEdit = useCallback(() => {
    try {
      if (drawInstanceRef.current && drawFeatureId) {
        try { drawInstanceRef.current.delete(drawFeatureId); } catch {}
      }
    } catch (_) {}
    setIsGeometryEditing(false);
    setDrawFeatureId(null);
  }, [drawFeatureId]);

  // (removed inline FeatureList geometry edit handlers; geometry edit is started from Edit modal button)

  // Determine if current user is club president
  const isPresident = useMemo(() => {
    // Prefer direct flag from backend
    if (clubData && typeof clubData.is_president === 'boolean') return clubData.is_president;
    if (!clubData || !currentUser) return false;
    // Fallback: compare president.id
    const presId = typeof clubData.president === 'object' ? clubData.president?.id : clubData.president;
    if (presId != null && Number(presId) === Number(currentUser.id)) return true;
    // Last resort: creator
    const createdById = typeof clubData.created_by === 'object' ? clubData.created_by?.id : clubData.created_by;
    return Number(createdById) === Number(currentUser.id);
  }, [clubData, currentUser]);

  const startClubViewEdit = useCallback(() => {
    clearClubViewExtent(mapInstanceRef.current);
    setClubConfirmingView(true);
  }, []);

  const cancelClubViewEdit = useCallback(() => {
    const map = mapInstanceRef.current;
    const saved = resolveClubMapState(clubData?.map || {});
    if (map && hasCanonicalClubViewport(saved)) {
      const jump = buildClubJumpToOptions(saved.state);
      if (jump) {
        try { map.jumpTo(jump); } catch (_) {}
      }
      applyClubViewExtent(map, saved.state, { captureLiveBounds: true });
    }
    setClubConfirmingView(false);
  }, [clubData]);

  const confirmClubView = useCallback(async () => {
    const map = mapInstanceRef.current;
    const state = clubCameraStateFromMap(map);
    if (!state || !clubId) return;
    try {
      setClubViewSaving(true);
      const bbox = resolveClubBoundaryBbox(clubData?.map || {}, huntBoundaries);
      const res = await api.post(`/clubs/${clubId}/viewport/`, {
        map_state: state,
        ...(bbox ? { boundary_bbox: bbox } : {}),
      });
      if (res?.data) setClubData(res.data);
      writeClubCameraCache(clubId, res?.data?.map?.viewport_version, state);
      applyClubViewExtent(map, state, { captureLiveBounds: true });
      setClubConfirmingView(false);
    } catch (e) {
      try {
        show({
          title: 'Could not save club view',
          body: e?.response?.data?.error || e.message || 'Try again.',
          variant: 'warning',
        });
      } catch (_) {
        alert(e?.response?.data?.error || e.message || 'Could not save club view');
      }
    } finally {
      setClubViewSaving(false);
    }
  }, [clubId, clubData, huntBoundaries, show]);

  useEffect(() => {
    if (shouldEnterConfirmClubView({
      isPresident,
      tractCount: clubTracts(huntBoundaries).length,
      hasSavedView: hasSavedClubView(clubData?.map),
      confirming: clubConfirmingView,
    })) {
      clearClubViewExtent(mapInstanceRef.current);
      setClubConfirmingView(true);
    }
  }, [isPresident, huntBoundaries, clubData, clubConfirmingView]);

  const cancelClubCreateModal = useCallback(() => {
    setShowClubCreateModal(false);
    setClubDraftIsBoundary(true);
    setClubParcelClusters([]);
    setClubDraftFeature(null);
    if (clubTracts(huntBoundaries).length === 0) {
      setClubLandPath(null);
    }
  }, [huntBoundaries]);

  // Prepare club members for SignOutModal
  const clubMembers = useMemo(() => {
    if (!clubData?.members || !currentUser) {
      console.log('=== CLUB MEMBERS CHECK FAILED ===');
      console.log('clubData?.members:', clubData?.members);
      console.log('currentUser:', currentUser);
      return [];
    }
    
    const members = clubData.members.map(member => ({
      ...member,
      is_current_user: member.id === currentUser.id
    }));
    
    console.log('=== CLUB MEMBERS PROCESSED ===');
    console.log('Original members:', clubData.members);
    console.log('Processed members:', members);
    console.log('Current user ID:', currentUser.id);
    
    return members;
  }, [clubData, currentUser]);

  useEffect(() => {
    if (mapType !== 'club') {
      listFeatures();
      return undefined;
    }
    setClubCameraReady(false);
    setHasFittedToClubBounds(false);
    setClubConfirmingView(false);
    let cancelled = false;
    (async () => {
      try {
        const result = await listClubFeatures();
        if (!cancelled && result?.club) setClubData(result.club);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [mapType, clubId, listClubFeatures, listFeatures]);

  useEffect(() => {
    if (!embedStandBoard || mapType !== 'club' || !clubId) return undefined;
    let cancelled = false;
    feedingService
      .getClubFeedingOverview(clubId)
      .then((data) => {
        if (!cancelled) setClubFeedSeasonLbs(Number(data?.season_lbs) || 0);
      })
      .catch(() => {
        if (!cancelled) setClubFeedSeasonLbs(0);
      });
    return () => {
      cancelled = true;
    };
  }, [embedStandBoard, mapType, clubId, huntLocations]);

  const handlePersonalFillLogged = useCallback(async (amountLbs) => {
    try {
      show({ title: clubFillLoggedToast(amountLbs), variant: 'success', duration: 3000 });
    } catch (_) {}
    try {
      await listFeatures();
    } catch (_) {}
  }, [listFeatures, show]);

  const handleClubFillLogged = useCallback(async (amountLbs) => {
    try {
      show({ title: clubFillLoggedToast(amountLbs), variant: 'success', duration: 3000 });
    } catch (_) {}
    try {
      await listClubFeatures();
    } catch (_) {}
    if (!clubId) return;
    try {
      const data = await feedingService.getClubFeedingOverview(clubId);
      setClubFeedSeasonLbs(Number(data?.season_lbs) || 0);
    } catch (_) {
      setClubFeedSeasonLbs(0);
    }
  }, [clubId, listClubFeatures, show]);

  const clearStandParam = useCallback(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      if (!params.has('standId')) return;
      params.delete('standId');
      const next = params.toString();
      navigate(
        { pathname: location.pathname, search: next ? `?${next}` : '' },
        { replace: true }
      );
    } catch (_) {}
  }, [location.pathname, location.search, navigate]);

  const clearFeatureParam = useCallback(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      if (!params.has('featureId')) return;
      params.delete('featureId');
      const next = params.toString();
      navigate(
        { pathname: location.pathname, search: next ? `?${next}` : '' },
        { replace: true }
      );
    } catch (_) {}
  }, [location.pathname, location.search, navigate]);

  const clearStandParamRef = useRef(clearStandParam);
  clearStandParamRef.current = clearStandParam;
  const clearFeatureParamRef = useRef(clearFeatureParam);
  clearFeatureParamRef.current = clearFeatureParam;

  // Helper: clear parcel_* query params via history replace (no navigation jump)
  const clearParcelParams = useCallback(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const before = params.toString();
      ['parcel_bbox', 'parcel_ll', 'parcel_id', 'parcel_path', 'parcel_center'].forEach((k) => params.delete(k));
      const after = params.toString();
      if (before !== after) {
        navigate({ pathname: location.pathname, search: after ? `?${after}` : '' }, { replace: true });
      }
    } catch (_) {}
  }, [location.pathname, location.search, navigate]);

  // Focus the map if parcel_bbox is provided in the query string
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    try {
      const params = new URLSearchParams(location.search || '');
      const bboxStr = params.get('parcel_bbox');
      if (!bboxStr) return;
      const parts = bboxStr.split(',').map(Number);
      if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return;
      const [w, s, e, n] = parts;
      const bounds = new mapboxgl.LngLatBounds([w, s], [e, n]);
      // Skip animated move if BaseMap already focused immediately to avoid jolt
      if (!map.__yona_parcelFocused) {
        map.fitBounds(bounds, { padding: 50, maxZoom: 17, duration: 1000 });
      }
      // Verify focus then clear parcel params (one-shot)
      try {
        const verifyAndClear = () => {
          try {
            const cur = map.getBounds && map.getBounds();
            if (cur && cur.contains([w, s]) && cur.contains([e, n])) {
              clearParcelParams();
            }
          } catch (_) {}
        };
        map.once && map.once('idle', verifyAndClear);
      } catch (_) {}
      // Prevent subsequent auto-fit from overriding
      setHasFittedToPersonalFeatures(true);
      // Add a temporary bbox highlight
      const rect = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [w, s], [e, s], [e, n], [w, n], [w, s]
            ]]
          }
        }]
      };
      try {
        if (!map.getSource('parcel-focus')) {
          map.addSource('parcel-focus', { type: 'geojson', data: rect });
        } else {
          map.getSource('parcel-focus').setData(rect);
        }
        if (!map.getLayer('parcel-focus-fill')) {
          map.addLayer({
            id: 'parcel-focus-fill',
            type: 'fill',
            source: 'parcel-focus',
            paint: { 'fill-color': '#ffff00', 'fill-opacity': 0.15 }
          });
        }
        if (!map.getLayer('parcel-focus-outline')) {
          map.addLayer({
            id: 'parcel-focus-outline',
            type: 'line',
            source: 'parcel-focus',
            paint: { 'line-color': '#ffffff', 'line-width': 2.0 }
          });
        }
        // Auto-clear after a few seconds
        setTimeout(() => {
          try {
            if (map.getLayer('parcel-focus-outline')) map.removeLayer('parcel-focus-outline');
            if (map.getLayer('parcel-focus-fill')) map.removeLayer('parcel-focus-fill');
            if (map.getSource('parcel-focus')) map.removeSource('parcel-focus');
          } catch (_) {}
        }, 5000);
      } catch (_) {}
    } catch (_) {}
  }, [location.search, mapReadyTick, clearParcelParams]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const removeMarker = () => {
      if (highlightStandMarkerRef.current) {
        try { highlightStandMarkerRef.current.remove(); } catch (_) {}
        highlightStandMarkerRef.current = null;
      }
    };

    if (!map) {
      removeMarker();
      return undefined;
    }

    let coords = null;
    let isPickerHighlight = false;
    let onFocused = null;

    if (mapType === 'club') {
      const activeHighlightId = showTagOutPicker ? pickerFocusedStandId : highlightStandId;
      isPickerHighlight = Boolean(showTagOutPicker && pickerFocusedStandId);
      if (!activeHighlightId) {
        removeMarker();
        return undefined;
      }
      const target = Array.isArray(huntLocations)
        ? huntLocations.find((loc) => Number(loc.id) === Number(activeHighlightId))
        : null;
      if (!target?.geojson || target.geojson.type !== 'Point') {
        removeMarker();
        return undefined;
      }
      coords = target.geojson.coordinates;
      if (!isPickerHighlight) {
        onFocused = () => {
          setHasFittedToClubBounds(true);
          try {
            map.once('moveend', () => clearStandParamRef.current());
          } catch (_) {
            clearStandParamRef.current();
          }
        };
      }
    } else if (mapType === 'personal') {
      if (!highlightFeatureId) {
        removeMarker();
        return undefined;
      }
      const target = Array.isArray(personalFeatures)
        ? personalFeatures.find((feature) => Number(feature.id) === Number(highlightFeatureId))
        : null;
      if (!target?.geojson || target.geojson.type !== 'Point') {
        removeMarker();
        return undefined;
      }
      coords = target.geojson.coordinates;
      onFocused = () => {
        setHasFittedToPersonalFeatures(true);
        clearFeatureParamRef.current();
      };
    } else {
      removeMarker();
      return undefined;
    }

    if (!Array.isArray(coords) || coords.length < 2) {
      removeMarker();
      return undefined;
    }

    const el = document.createElement('div');
    el.className = isPickerHighlight
      ? 'stand-highlight-pulse stand-highlight-picker'
      : 'stand-highlight-pulse';
    const core = document.createElement('div');
    core.className = 'stand-highlight-core';
    el.appendChild(core);
    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(coords)
      .addTo(map);
    highlightStandMarkerRef.current = marker;

    try {
      const currentZoom = map.getZoom ? map.getZoom() : 15;
      if (isPickerHighlight) {
        const easeOpts = buildPickerHighlightEaseOptions(
          coords,
          Math.max(currentZoom, 16),
          pickerSheetHeight
        );
        map.easeTo(easeOpts);
      } else {
        map.flyTo({ center: coords, zoom: Math.max(currentZoom, 16), duration: 800 });
      }
    } catch (_) {}

    if (typeof onFocused === 'function') {
      onFocused();
    }

    return () => removeMarker();
  }, [
    highlightStandId,
    highlightFeatureId,
    huntLocations,
    personalFeatures,
    mapType,
    mapReadyTick,
    showTagOutPicker,
    pickerFocusedStandId,
    pickerSheetHeight,
  ]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || showTagOutPicker) return undefined;
    if (mapType === 'club' && highlightStandId) {
      const handleMapClick = () => setHighlightStandId(null);
      map.on('click', handleMapClick);
      return () => {
        try { map.off('click', handleMapClick); } catch (_) {}
      };
    }
    if (mapType === 'personal' && highlightFeatureId) {
      const handleMapClick = () => setHighlightFeatureId(null);
      map.on('click', handleMapClick);
      return () => {
        try { map.off('click', handleMapClick); } catch (_) {}
      };
    }
    return undefined;
  }, [highlightStandId, highlightFeatureId, mapType, showTagOutPicker]);
  // If user starts moving, clear parcel params to avoid snap-back
  // Attach only when parcel params exist, and prefer attaching after first focus
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (!hasParcelParams) return;
    const onMoveStart = () => {
      try { clearParcelParams(); } catch (_) {}
      try { map.off && map.off('movestart', onMoveStart); } catch (_) {}
    };
    const attach = () => {
      try { map.on('movestart', onMoveStart); } catch (_) {}
    };
    try {
      if (map.__yona_parcelFocused) {
        attach();
      } else if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
        map.once && map.once('idle', attach);
      } else {
        // brief delay to allow initial focus effects to run first
        requestAnimationFrame(() => requestAnimationFrame(attach));
      }
    } catch (_) {
      attach();
    }
    return () => { try { map.off('movestart', onMoveStart); } catch (_) {} };
  }, [hasParcelParams, clearParcelParams]);

  // Prevent auto-fit override when only parcel_center is provided
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    try {
      const params = new URLSearchParams(location.search || '');
      const hasCenter = params.has('parcel_center');
      const hasBbox = params.has('parcel_bbox');
      if (hasCenter && !hasBbox) {
        setHasFittedToPersonalFeatures(true);
      }
    } catch (_) {}
  }, [location.search]);
  // If only parcel_center is provided, move camera accordingly (Chrome robustness)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    try {
      const params = new URLSearchParams(location.search || '');
      const ctrStr = params.get('parcel_center');
      const hasBbox = params.has('parcel_bbox');
      if (!ctrStr || hasBbox) return;
      const arr = ctrStr.split(',').map(Number);
      if (arr.length !== 2 || arr.some((n) => Number.isNaN(n))) return;
      const moveToCenter = () => {
        try {
          if (map.__yona_parcelFocused) return;
          map.easeTo({ center: arr, zoom: Math.max(15, (map.getZoom ? map.getZoom() : 15)), duration: 600 });
        } catch (_) {}
      };
      try {
        if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
          map.once && map.once('idle', moveToCenter);
        } else {
          requestAnimationFrame(() => requestAnimationFrame(moveToCenter));
        }
      } catch (_) { moveToCenter(); }
      // Verify focus then clear parcel params (one-shot)
      try {
        const verifyAndClear = () => {
          try {
            const c = map.getCenter && map.getCenter();
            if (c && Math.abs(c.lng - arr[0]) < 0.0008 && Math.abs(c.lat - arr[1]) < 0.0008) {
              clearParcelParams();
            }
          } catch (_) {}
        };
        map.once && map.once('idle', verifyAndClear);
      } catch (_) {}
      setHasFittedToPersonalFeatures(true);
    } catch (_) {}
  }, [location.search, mapReadyTick, clearParcelParams]);
  // Clear club extent when switching to personal map to avoid constraining navigation
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (mapType === 'personal') {
      clearClubViewExtent(map);
    }
  }, [mapType]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const fillLayerId = 'area-group-highlight-fill';
    const lineLayerId = 'area-group-highlight-line';
    const removeLayers = () => {
      try { if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId); } catch (_) {}
      try { if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId); } catch (_) {}
    };

    if (!highlightGroup) {
      removeLayers();
      return;
    }

    const combinedIds = [
      ...((highlightGroup.personalPolygonIds || []).map(String)),
      ...((highlightGroup.clubBoundaryIds || []).map(String))
    ].filter(Boolean);

    if (combinedIds.length === 0) {
      removeLayers();
      clearHighlightGroup();
      return;
    }

    const applyHighlight = () => {
      if (!map.getSource('features-polygons')) {
        setTimeout(applyHighlight, 250);
        return;
      }
      const filterExpr = ['in', 'id', ...combinedIds];

      if (!map.getLayer(fillLayerId)) {
        try {
          map.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: 'features-polygons',
            paint: {
              'fill-color': '#fffbcc',
              'fill-opacity': 0.35
            }
          }, 'features-polygons-outline');
        } catch (_) {}
      }
      if (!map.getLayer(lineLayerId)) {
        try {
          map.addLayer({
            id: lineLayerId,
            type: 'line',
            source: 'features-polygons',
            paint: {
              'line-color': '#f7b500',
              'line-width': 4
            }
          }, 'features-polygons-outline');
        } catch (_) {}
      }
      try { map.setFilter(fillLayerId, filterExpr); } catch (_) {}
      try { map.setFilter(lineLayerId, filterExpr); } catch (_) {}

      try {
        const matches = filteredFeatures.filter((f) => combinedIds.includes(String(f.id)));
        if (matches.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          matches.forEach((feature) => extendBoundsWithGeometry(bounds, feature.geojson || feature.geometry));
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds, { padding: 80, duration: 600 });
          }
        }
      } catch (_) {}
    };

    if (map.isStyleLoaded()) {
      applyHighlight();
    } else {
      const handler = () => applyHighlight();
      map.once('styledata', handler);
    }

    return () => {
      removeLayers();
    };
  }, [highlightGroup, clearHighlightGroup, filteredFeatures, extendBoundsWithGeometry]);

  useEffect(() => {
    if (mapType !== 'personal') return undefined;
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    const apply = () => applySelectedFeatureLayers(map, selectedFeatureId);
    if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
      map.once('styledata', apply);
      return () => {
        try { map.off('styledata', apply); } catch (_) {}
      };
    }
    apply();
    return undefined;
  }, [mapType, selectedFeatureId, mapReadyTick]);

  // --- Club camera: saved view as widest extent; else fit every tract ---
  useEffect(() => {
    if (mapType !== 'club') return undefined;
    const map = mapInstanceRef.current;
    if (!map || !clubData) return undefined;
    if (hasStandParam || highlightStandId) {
      markClubCameraReady();
      return undefined;
    }
    if (hasFittedToClubBounds) return undefined;

    try {
      const mapInfo = clubData?.map || {};
      const saved = resolveClubMapState(mapInfo);
      if (!clubConfirmingView && hasCanonicalClubViewport(saved)) {
        clearClubViewExtent(map);
        const jump = buildClubJumpToOptions(saved.state);
        if (jump) {
          try { map.jumpTo(jump); } catch (_) {}
        }
        applyClubViewExtent(map, saved.state, { captureLiveBounds: true });
        setHasFittedToClubBounds(true);
        try { map.__yona_initialViewStrategy = 'canonical'; } catch (_) {}
        markClubCameraReady();
        return undefined;
      }

      const rawBbox = resolveClubBoundaryBbox(mapInfo, huntBoundaries);
      if (rawBbox) {
        clearClubViewExtent(map);
        try {
          map.fitBounds(
            [[rawBbox[0], rawBbox[1]], [rawBbox[2], rawBbox[3]]],
            clubBoundaryFitBoundsOptions({ compact: isMobileMd })
          );
        } catch (_) {}
        setHasFittedToClubBounds(true);
        try { map.__yona_initialViewStrategy = 'boundary'; } catch (_) {}
        markClubCameraReady();
      } else if (!clubFeaturesLoading && clubTracts(huntBoundaries).length === 0) {
        markClubCameraReady();
      }
    } catch (_) {}

    return undefined;
  }, [
    mapType,
    clubId,
    clubData,
    huntBoundaries,
    hasStandParam,
    highlightStandId,
    clubFeaturesLoading,
    hasFittedToClubBounds,
    isMobileMd,
    markClubCameraReady,
    mapReadyTick,
    clubConfirmingView,
  ]);

  // Saved view is the zoom-out floor; lift it while reframing or picking parcels.
  useEffect(() => {
    if (mapType !== 'club') return;
    const map = mapInstanceRef.current;
    if (!map) return;
    const extentOn = clubBrowseExtentActive({
      confirming: clubConfirmingView,
      hasSavedView: hasSavedClubView(clubData?.map),
    }) && !parcelSelectEnabled;
    if (!extentOn) {
      clearClubViewExtent(map);
      return;
    }
    const saved = resolveClubMapState(clubData?.map || {});
    if (hasCanonicalClubViewport(saved)) {
      applyClubViewExtent(map, saved.state);
    }
  }, [mapType, clubConfirmingView, parcelSelectEnabled, clubData, mapReadyTick]);

  // When entering draw modes, prevent future auto-fit until user is done
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const handler = (e) => {
      const mode = e && e.mode;
      const isDrawing = Boolean(mode && (mode.indexOf('draw_') === 0 || mode === 'direct_select' || mode === 'simple_select'));
      try { map.__yona_isDrawing = isDrawing; } catch(_) {}
      if (mode && mode !== 'draw_point') {
        try { map.__yona_creationContext = null; } catch (_) {}
      }
      try { map.__yona_syncPlacementToolChrome && map.__yona_syncPlacementToolChrome(); } catch (_) {}
      if (isDrawing) {
        // Prevent any pending/next auto-fit behavior while user is drawing
        setHasFittedToClubBounds(true);
        setHasFittedToPersonalFeatures(true);
      }
    };
    try { map.on('draw.modechange', handler); } catch(_) {}
    return () => { try { map.off('draw.modechange', handler); } catch(_) {} };
  }, []);

  // (getClubMapId moved above)

  const isPersonalWorkspace = mapType !== 'club';
  const framePersonalMap = isPersonalWorkspace && !mapMaximized;

  const showClubMapEmbed = embedStandBoard && mapType === 'club';

  return (
    <div
      className={
        showClubMapEmbed
          ? `club-map-tab${mapMaximized ? ' club-map-tab--maximized' : ''}`
          : isPersonalWorkspace
            ? `my-ground-workspace${mapMaximized ? ' my-ground-workspace--maximized' : ''}${!mapMaximized ? ' my-ground-workspace--board' : ''}`
            : 'club-map-page'
      }
      data-testid={showClubMapEmbed ? 'club-map-tab' : undefined}
    >
    {/* display:contents slot keeps My Ground flex chain; real frame only for club embed */}
    <div className={showClubMapEmbed ? 'club-map-tab__frame' : 'map-layout-slot'}>
    <div
      className={`map-container${framePersonalMap ? ' map-container--framed' : ''}${
        showClubMapEmbed ? ' map-container--club-embed' : ''
      }`}
    >
    {shouldShowClubLandSetup({
      isPresident,
      tractCount: clubTracts(huntBoundaries).length,
      setupPath: clubLandPath,
    }) && !clubFeaturesLoading && clubCameraReady ? (
      <ClubLandSetupOverlay show onChoose={chooseClubLandPath} />
    ) : null}
    <ClubConfirmViewBar
      show={clubConfirmingView && isPresident}
      canCancel={hasSavedClubView(clubData?.map)}
      saving={clubViewSaving}
      onConfirm={confirmClubView}
      onCancel={cancelClubViewEdit}
    />
    {mapType === 'club' && !clubCameraReady && (
      <div className="map-initial-loading" aria-busy="true" aria-label="Loading map">
        <Spinner animation="border" role="status" />
      </div>
    )}
    {highlightGroup && (
      <div className="map-highlight-banner">
        <div>
          Viewing <strong>{highlightGroup.name}</strong>
        </div>
        <Button variant="light" size="sm" onClick={clearHighlightGroup}>
          Clear
        </Button>
      </div>
    )}

      {/* Map search: fly the camera only — never saves a pin */}
      <div className="map-search-bar">
        <PlaceSearchInput
          placeholder="Search address or place…"
          onSelect={(place) => {
            const map = mapInstanceRef.current;
            if (!map) return;
            try {
              map.stop && map.stop();
            } catch (_) {}
            const lng = place.longitude;
            const lat = place.latitude;
            if (place.bounds && Array.isArray(place.bounds.sw) && Array.isArray(place.bounds.ne)) {
              const bounds = new mapboxgl.LngLatBounds(place.bounds.sw, place.bounds.ne);
              map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 800 });
            } else {
              map.flyTo({ center: [lng, lat], zoom: 15, duration: 800 });
            }
          }}
          className="map-search-bar__input"
        />
      </div>

      {typeof onToggleMapMaximize === 'function' && (
        <MyGroundMaximizeControl
          maximized={mapMaximized}
          onToggle={onToggleMapMaximize}
        />
      )}

      {/* Map */}
      <BaseMap
        key={`basemap-${mapType}-${activeBaseMapStyle}`}
        baseMapStyle={activeBaseMapStyle}
        fieldEditionStandPins={mapType === 'club'}
        showParcelsControl
        canEnableParcels={canEnableParcels}
        forceParcelsOn={mapType === 'club' && clubLandPath === 'parcels'}
        onParcelsProRequired={() => setShowParcelsPro(true)}
        mapConfig={mapType === 'club' ? (clubInitialMapConfig || { usePersonalSavedView: false }) : { projection: 'globe' }}
        features={filteredFeatures}
        onMapReady={handleMapReady}
        onMapMove={handleMapMove}
        onFeaturesRendered={handleFeaturesRendered}
        onDrawCreate={handleDrawCreate}
        onDrawUpdate={handleDrawUpdate}
        onDrawDelete={handleDrawDelete}
        onFeatureClick={handleFeatureClick}
        onParcelInfoRequest={mapType === 'club' ? undefined : handleParcelInfoRequested}
        onParcelsVisibilityChange={setParcelsVisible}
        parcelIdentifyEnabled={mapType !== 'club' && parcelIdentifyOn}
        parcelSelectionEnabled={parcelSelectEnabled}
        onParcelSelectToggle={handleParcelSelectToggle}
        onRequestParcelBoundaryCreate={() => {
          try { console.log('[parcel] onRequestParcelBoundaryCreate fired', { parcelSelectEnabled, selectedCount: (selectedParcels && selectedParcels.length) || 0 }); } catch(_) {}
          // Behavior:
          // - If not selecting yet: enable selection
          // - If selecting and have selection: save boundary
          // - If selecting and no selection: exit selection mode
          if (!parcelSelectEnabled) {
            toggleParcelSelectMode(true);
            return;
          }
          const hasSel = Array.isArray(selectedParcels) && selectedParcels.length > 0;
          if (hasSel) {
            try { console.log('[parcel] invoking handleParcelDone'); } catch(_) {}
            handleParcelDone();
            return;
          }
          // Exit selection mode
          try { console.log('[parcel] exiting selection (no parcels selected)'); } catch(_) {}
          toggleParcelSelectMode(false);
        }}
        onExitParcelSelection={exitParcelSelection}
        hasParcelSelection={Array.isArray(selectedParcels) && selectedParcels.length > 0}
        isParcelCreateBusy={unionBusy}
        closeParcelsPopoverSignal={closeParcelsPopoverSignal}
        selectedParcelIds={selectedParcels.map(p => String(p.parcelId))}
        previewUnionGeometry={unionPreview}
        drawingMode={drawingMode}
        currentWindDegrees={currentWindDeg}
        onStartDropWaypoint={startDropWaypoint}
        canDraw={mapType === 'club' ? !!isPresident : true}
        blockedDrawMessage={'Only presidents can add to the club map.'}
      />

      <ParcelTapDebug
        show={isParcelDebugEnabled()}
        mapRef={mapInstanceRef}
        isPhone={isMobileMd}
        parcelsVisible={parcelsVisible}
        identifyOn={parcelIdentifyOn}
        lotSelectionOn={parcelSelectEnabled}
        drawingMode={drawingMode}
      />

      {/* Bottom-right widgets (distance + weather) */}
      <div className="weather-widget-container">
        <div className="widget-stack">
          {/* Floating Done/Cancel removed; use the Parcels popover Create boundary and sliders */}
          {isGeometryEditing && (
            <div className="geometry-edit-controls">
              <button type="button" className="geometry-action-btn save" onClick={saveGeometryEdit}>Save Drawing</button>
              <button type="button" className="geometry-action-btn cancel" onClick={cancelGeometryEdit}>Cancel</button>
            </div>
          )}
          {realTimeDistance != null && (
            <div className="distance-display distance-floating">{formatDistance(realTimeDistance)}</div>
          )}
          {/* Belongs to the selection mode, so it lives in the stack: at the old
              bottom-left/z-index 3 it rendered under the widgets. Above the
              chips, never below, so appearing cannot move them mid-tap. */}
          {!contiguousOk && parcelSelectEnabled && (
            <div className="parcel-contiguity-warning" role="alert" data-testid="parcel-contiguity-warning">
              These parcels are too far apart for one area. Select neighboring lots
              (a road between them is fine), or save this group and create another area.
            </div>
          )}
          {mapType === 'club' ? (
            <div className="club-map-chrome-row">
              <WeatherWidget
                latitude={mapCenter.lat}
                longitude={mapCenter.lng}
                userId={currentUser?.id}
                className="weather-widget-overlay"
              />
              <ClubMapLegend />
            </div>
          ) : (
            <>
              {/* Weather steps aside while either parcel mode is armed: bottom
                  space is tight on a phone and weather is irrelevant mid parcel
                  work. Both flags are phone-only, so desktop keeps its chip. */}
              {!parcelIdentifyOn && !showLotSelectControl && (
                <WeatherWidget
                  latitude={mapCenter.lat}
                  longitude={mapCenter.lng}
                  userId={currentUser?.id}
                  className="weather-widget-overlay"
                />
              )}
              {/* Last in a bottom-anchored stack, so losing weather above it
                  does not move the control out from under the user's thumb.
                  Identify and lot-select are mutually exclusive modes. */}
              <ParcelIdentifyControl
                show={showParcelIdentifyControl}
                active={parcelIdentifyOn}
                onToggle={toggleParcelIdentify}
              />
              <ParcelLotSelectControl
                show={showLotSelectControl}
                count={selectedParcels.length}
                busy={unionBusy}
                onDone={handleParcelDone}
                onCancel={exitParcelSelection}
              />
            </>
          )}
        </div>
      </div>

      {/* Feature Tooltip */}
      {showParcelsPro ? (
        <div className="pro-panel-map-overlay" data-testid="parcels-pro-gate">
          <ProPanel onDismiss={() => setShowParcelsPro(false)} />
        </div>
      ) : null}
      <FeatureTooltip map={mapInstanceRef.current} features={allFeatures} mapType={mapType} canEnableFeed={canEnableFeed} />

    {/* Create Feature Modal */}
    <CreateFeatureModal
      show={showCreateModal}
      onHide={handleCancelCreate}
      draftFeature={draftFeature}
      draftName={draftName}
      setDraftName={setDraftName}
      draftNotes={draftNotes}
      setDraftNotes={setDraftNotes}
      draftIcon={draftIcon}
      setDraftIcon={setDraftIcon}
      draftRadius={draftRadius}
      setDraftRadius={setDraftRadius}
      draftOptimalWinds={draftOptimalWinds}
      setDraftOptimalWinds={setDraftOptimalWinds}
      draftIsStand={draftIsStand}
      setDraftIsStand={setDraftIsStand}
      draftLineColor={mapType === 'club' ? clubDraftLineColor : draftLineColor}
      setDraftLineColor={mapType === 'club' ? setClubDraftLineColor : setDraftLineColor}
      draftFillColor={mapType === 'club' ? clubDraftFillColor : draftFillColor}
      setDraftFillColor={mapType === 'club' ? setClubDraftFillColor : setDraftFillColor}
      draftFillOpacity={mapType === 'club' ? clubDraftFillOpacity : draftFillOpacity}
      setDraftFillOpacity={mapType === 'club' ? setClubDraftFillOpacity : setDraftFillOpacity}
      onSave={handleSaveCreate}
      onCancel={handleCancelCreate}
      isClubBoundary={mapType === 'club'}
      clubId={clubId}
      isClubMap={mapType === 'club'} // Add this line
    />

    {/* Edit Feature Modal */}
    <EditFeatureModal
      show={showEditModal}
      onHide={handleCancelEdit}
      editingFeature={editingFeature}
      editName={editName}
      setEditName={setEditName}
      editNotes={editNotes}
      setEditNotes={setEditNotes}
      editIcon={editIcon}
      setEditIcon={setEditIcon}
      editRadius={editRadius}
      setEditRadius={setEditRadius}
      editOptimalWind={editOptimalWind}
      setEditOptimalWind={setEditOptimalWind}
      editOptimalWinds={editOptimalWinds}
      setEditOptimalWinds={setEditOptimalWinds}
      editIsStand={editIsStand}
      setEditIsStand={setEditIsStand}
      editLineColor={editLineColor}
      setEditLineColor={setEditLineColor}
      editFillColor={editFillColor}
      setEditFillColor={setEditFillColor}
      editFillOpacity={editFillOpacity}
      setEditFillOpacity={setEditFillOpacity}
        editFeedingMode={editFeedingMode}
        setEditFeedingMode={setEditFeedingMode}
        editFeedCapacityLbs={editFeedCapacityLbs}
        setEditFeedCapacityLbs={setEditFeedCapacityLbs}
        editConsumptionRateCode={editConsumptionRateCode}
        setEditConsumptionRateCode={setEditConsumptionRateCode}
        editRefillThresholdPct={editRefillThresholdPct}
        setEditRefillThresholdPct={setEditRefillThresholdPct}
      onSave={handleSaveEdit}
      onCancel={handleCancelEdit}
      /* Geometry edit controls are now exclusively in the feature list map-edit flow */
    />

    {/* Club Edit Feature Modal (president) */}
    {mapType === 'club' && isPresident && (
      <EditFeatureModal
        show={showClubEditModal}
        onHide={cancelClubEdit}
        editingFeature={clubEditingFeature}
        editName={clubEditName}
        setEditName={setClubEditName}
        editNotes={clubEditNotes}
        setEditNotes={setClubEditNotes}
        editIcon={null}
        setEditIcon={() => {}}
        editRadius={clubEditRadius}
        setEditRadius={setClubEditRadius}
        onSave={saveClubEdit}
        onCancel={cancelClubEdit}
        onDelete={() => requestClubLandDelete(clubEditingFeature)}
        onStartGeometryEdit={startClubShapeFromEditor}
        onSaveGeometry={saveGeometryEdit}
        isGeometryEditing={isGeometryEditing}
        editFeatureType={clubEditFeatureType}
        setEditFeatureType={setClubEditFeatureType}
        editOptimalWind={clubEditOptimalWind}
        setEditOptimalWind={setClubEditOptimalWind}
        editOptimalWinds={clubEditOptimalWinds}
        setEditOptimalWinds={setClubEditOptimalWinds}
        editLineColor={clubEditLineColor}
        setEditLineColor={setClubEditLineColor}
        editFillColor={clubEditFillColor}
        setEditFillColor={setClubEditFillColor}
        editFillOpacity={clubEditFillOpacity}
          setEditFillOpacity={setClubEditFillOpacity}
        editIsBoundary={clubEditIsBoundary}
        setEditIsBoundary={setClubEditIsBoundary}
        // Feeding (club)
        editFeedingMode={clubEditingFeature?.feeding_mode || ''}
        setEditFeedingMode={(v) => setClubEditingFeature(f => ({ ...f, feeding_mode: v }))}
        editFeedCapacityLbs={clubEditingFeature?.feed_capacity_lbs || null}
        setEditFeedCapacityLbs={(v) => setClubEditingFeature(f => ({ ...f, feed_capacity_lbs: v }))}
        editConsumptionRateCode={clubEditingFeature?.consumption_rate_code || ''}
        setEditConsumptionRateCode={(v) => setClubEditingFeature(f => ({ ...f, consumption_rate_code: v }))}
        editRefillThresholdPct={typeof clubEditingFeature?.refill_threshold_pct === 'number' ? clubEditingFeature.refill_threshold_pct : 20}
        setEditRefillThresholdPct={(v) => setClubEditingFeature(f => ({ ...f, refill_threshold_pct: v }))}
      />
    )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        feature={featureToDelete}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmModal
        show={Boolean(clubLandDeleteTarget)}
        onCancel={cancelClubLandDelete}
        onConfirm={confirmClubLandDelete}
        title={clubLandDeleteTarget?.plan?.title || 'Delete club land'}
        body={clubLandDeleteTarget?.plan?.body || ''}
        confirmLabel={clubLandDeleteBusy ? 'Deleting…' : 'Delete'}
        confirmTestId="club-land-delete-confirm"
      />

      <ConfirmModal
        show={Boolean(clubWaypointDeleteTarget)}
        onCancel={cancelClubWaypointDelete}
        onConfirm={confirmClubWaypointDelete}
        title={clubWaypointDeleteTarget?.plan?.title || 'Delete waypoint'}
        body={clubWaypointDeleteTarget?.plan?.body || ''}
        confirmLabel={clubWaypointDeleteBusy ? 'Deleting…' : 'Delete'}
        confirmTestId="club-waypoint-delete-confirm"
      />

      {/* Parcel Info Request Modal */}
      <ParcelInfoRequestModal
        show={showParcelModal}
        onHide={() => { setShowParcelModal(false); setPendingParcel(null); }}
        parcel={pendingParcel}
      />

      {/* Sign Out Modal */}
      <SignOutModal
        show={showSignOutModal}
        location={selectedLocation}
        occupancy={sheetOccupancy}
        clubId={clubId}
        onSignOut={handleSignOut}
        onSignIn={handleSignIn}
        onClose={handleSignOutModalClose}
        onStandReleased={handleStandReleased}
        isPresident={isPresident}
        clubMembers={clubMembers}
        currentUser={currentUser}
        loading={signOutLoading}
        error={signOutError}
      />

      {mapType === 'club' && isMobileMap && !embedStandBoard && clubStands.length > 0 && !showTagOutPicker && (
        <button
          type="button"
          className="tag-out-fab y-btn y-btn-primary"
          onClick={openTagOutPicker}
        >
          Tag out
        </button>
      )}

      {mapType === 'club' && isMobileMap && !embedStandBoard && (
        <TagOutPicker
          show={showTagOutPicker}
          stands={clubStands}
          focusedStandId={pickerFocusedStandId}
          selectedStandId={pickerSelectedStandId}
          onClose={closeTagOutPicker}
          onFocusStand={setPickerFocusedStandId}
          onSelectStand={setPickerSelectedStandId}
          onTagOut={handleTagOutFromPicker}
          onViewTagged={handleViewTaggedFromPicker}
          onSheetHeight={setPickerSheetHeight}
        />
      )}

      {/* Club Feature Create Modal */}
      {mapType === 'club' && (
        <CreateFeatureModal
          show={showClubCreateModal}
          onHide={cancelClubCreateModal}
          draftFeature={clubDraftFeature}
          draftName={clubDraftName}
          setDraftName={setClubDraftName}
          draftNotes={clubDraftNotes}
          setDraftNotes={setClubDraftNotes}
          draftIcon={clubDraftIcon}
          setDraftIcon={setClubDraftIcon}
          draftRadius={clubDraftRadius}
          setDraftRadius={setClubDraftRadius}
          onSave={handleClubFeatureSave}
          onCancel={cancelClubCreateModal}
          isClubMap={true}
          clubId={clubId}
          // Add the missing club-specific props
          draftFeatureType={clubDraftFeatureType}
          setDraftFeatureType={setClubDraftFeatureType}
          draftOptimalWinds={clubDraftOptimalWinds}
          setDraftOptimalWinds={setClubDraftOptimalWinds}
          // Polygon styling for club boundary create
          draftLineColor={clubDraftLineColor}
          setDraftLineColor={setClubDraftLineColor}
          draftFillColor={clubDraftFillColor}
          setDraftFillColor={setClubDraftFillColor}
          draftFillOpacity={clubDraftFillOpacity}
          setDraftFillOpacity={setClubDraftFillOpacity}
          isClubBoundary={clubDraftIsBoundary}
          setIsClubBoundary={setClubDraftIsBoundary}
        />
      )}

      {/* Marker Modal */}
      <MarkerModal
        show={showMarkerModal}
        onHide={cancelMarkerModal}
        editing={Boolean(editingMarkerId)}
        copy={mapType === 'club' ? 'club' : 'ground'}
        name={markerName}
        setName={setMarkerName}
        notes={markerNotes}
        setNotes={setMarkerNotes}
        isStand={markerIsStand}
        setIsStand={setMarkerIsStand}
        feed={markerFeed}
        setFeed={setMarkerFeed}
        markerType={markerType}
        setMarkerType={setMarkerType}
        winds={markerWinds}
        setWinds={setMarkerWinds}
        feedingMode={markerFeedingMode}
        setFeedingMode={setMarkerFeedingMode}
        feedCapacityLbs={markerFeedCapacityLbs}
        setFeedCapacityLbs={setMarkerFeedCapacityLbs}
        consumptionRateCode={markerConsumptionRateCode}
        setConsumptionRateCode={setMarkerConsumptionRateCode}
        refillThresholdPct={markerRefillThresholdPct}
        setRefillThresholdPct={setMarkerRefillThresholdPct}
        canEnableFeed={canEnableFeed}
        onSave={saveMarker}
        onCancel={cancelMarkerModal}
        onMove={mapType === 'club' && editingMarkerId ? startClubWaypointMoveFromEditor : undefined}
        onDelete={mapType === 'club' && editingMarkerId ? startClubWaypointDeleteFromEditor : undefined}
      />
    </div>
    </div>
    {isPersonalWorkspace && !mapMaximized ? (
      <MyGroundBoard
        features={allFeatures}
        hiddenFeatureIds={hiddenFeatureIds}
        selectedFeatureId={selectedFeatureId}
        onSelectFeature={handleFeatureClick}
        onEditFeature={handlePersonalEdit}
        onStartGeometryEdit={handleStartGeometryEdit}
        onDeleteFeature={(feature) => {
          handleDeleteRequest(feature);
        }}
        onToggleHidden={handleToggleHidden}
        onFillLogged={handlePersonalFillLogged}
        canEnableFeed={canEnableFeed}
      />
    ) : null}
    {/* Stands + feeders under the map. LOG FILL opens ClubLogFillDialog. */}
    {showClubMapEmbed && !mapMaximized ? (
      <ClubMapBoards
        stands={clubStands}
        feeders={clubWaypoints.feeders}
        markers={clubWaypoints.markers}
        currentUserId={currentUser?.id}
        selectedStandId={boardSelectedStandId}
        hoveredWaypointId={hoveredWaypointId}
        onHoverWaypoint={setHoveredWaypointId}
        onSelectStand={openStandFromBoard}
        onEditWaypoint={isPresident ? editClubBoardWaypoint : undefined}
        onMoveWaypoint={isPresident ? moveClubBoardWaypoint : undefined}
        onDeleteWaypoint={isPresident ? requestClubWaypointDelete : undefined}
        boundaries={huntBoundaries}
        selectedAreaId={boardSelectedAreaId}
        onSelectArea={focusClubAreaFromBoard}
        onEditArea={isPresident ? editClubBoardArea : undefined}
        onEditAreaShape={isPresident ? editClubBoardAreaShape : undefined}
        onDeleteArea={isPresident ? requestClubLandDelete : undefined}
        onEditView={isPresident ? startClubViewEdit : undefined}
        seasonLbs={clubFeedSeasonLbs}
        memberCount={clubData?.member_count ?? clubData?.members?.length ?? null}
        memberName={clubLogFillMemberName(currentUser, getDisplayName)}
        clubName={clubData?.name || ''}
        clubId={clubId}
        onFillLogged={handleClubFillLogged}
      />
    ) : null}
    </div>
  );
}
